// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Analyze panel — statistics over the frame on screen.
 *
 * `datasetProbe` answers "what is the number here"; this answers the
 * questions that need more than one texel, over a region the user
 * picks. See `docs/DATA_ANALYSIS_PLAN.md` §A3.
 *
 * Behind an explicit Tools entry rather than in the main chrome, per
 * the plan's settled audience decision: the colorbar, the threshold and
 * Orbit serve the museum floor, and a statistics table is not something
 * a casual visitor should have to dismiss to see the globe.
 *
 * Computation is user-initiated and one-shot. The whole frame is read
 * back once (~8 MB at 4096×2048) and every number here is a pure
 * function over that buffer — nothing on this surface runs per frame or
 * per pointer event.
 */

import {
  areaAboveKm2,
  buildHistogram,
  sampleLine,
  summarize,
  summarizeTransect,
  lineSampleCount,
  parallelSpanDeg,
  windowForBounds,
  zonalMeans,
  type LatLonBounds,
  type LumaHistogram,
  type RegionStats,
  type TexelWindow,
  type TransectEndpoints,
  type LinePath,
  type TransectSample,
  type ZonalSample,
} from '../services/datasetStats'
import { extractContourSet, type ContourLevel } from '../services/datasetContours'
import type { LumaSnapshot } from '../services/glLumaSampler'
import {
  DEFAULT_DISPLAY,
  buildDisplayLut,
  colorbarTicks,
  displayColorAtValue,
  type ColorScaleDisplay,
} from '../services/colorScaleDisplay'
import type { DisplayColorScale } from '../types/unit-scale'
import type { DatasetOverlayOptions } from '../types'
import { getRegionNames, resolveRegion } from '../data/regions'
import { COUNTRIES_SOURCE, countries as loadedCountries, countryById, countryCells, countryName, countryOutline, loadCountries, type Country } from '../data/countries'
import {
  formatStatValue,
  histogramBucketValueWidth,
  renderHistogram,
  renderStatTile,
  renderTransectChart,
  renderZonalChart,
} from './analyzeCharts'
import { buildCompareCsvText, buildCsvText, buildTransectCsvText, buildZonalCsvText, downloadCsv, type CsvBaseline } from './analyzeExport'
import { compareFields, isChangeTitle, linearStep, pairFor, quantityWarning, ratioRefusal, regionDifferences, unitConversion, type CompareField, type FramePairing, type FrameInstant, type RegionDifference } from '../services/research/compare'
import { buildLiveCompare, paintLiveCompare } from '../services/research/compareLive'
import type { MeasurementSeries } from '../services/measure/series'
import { ayniCsvFilename } from './pinsCsv'
import { getLocale, t, type MessageKey } from '../i18n'
import { formatDate, formatNumber } from '../i18n/format'
import { analysisExportsLocked, dataToolsAllowed, dataToolsLent, lineBoxLocked, regionStatsLocked, researchToolsAllowed, researchUpsellShown, anomaliesAllowed, teachingPlan } from '../services/planAccess'
import {
  CHANGE_GREY,
  CHANGE_PALETTE_IDS,
  PALETTE_STEPS,
  buildChangeLut,
  buildChangePalette,
  defaultChangeColours,
  symmetricRange,
  computeChangeGrid,
  computePeriodChangeGrid,
  valueTable,
  paintChangeGrid,
  percentMisleads,
  percentRefusal,
  floorRule,
  validRange,
  type ChangeGrid,
  type ChangeMode,
  type ChangeRange,
} from '../services/research/changeMap'
import { changeShown, changeViewState, setChangeView, shownRange, type BaselineField, type ChangeUpdate } from '../services/research/changeView'
import { currentPaletteDialog, togglePaletteDialog, type PaletteDialogHandle, type PaletteRamp } from './overlayPaletteDialog'
import {
  MIN_SIGMA_YEARS,
  NORMAL_YEARS,
  STANDARD_PERIOD,
  anomalySummary,
  axisYears,
  calendarFor,
  checkPeriod,
  clampPeriod,
  defaultPeriod,
  frameAtTime,
  climateFrameTimeMs,
  expectedGroups,
  frameOf,
  groupLabel,
  groupParts,
  periodFrames,
  periodMethodText,
  sampledTexel,
  type AnomalySummary,
  type ClimateAxis,
  type ClimateCalendar,
  type ClimatePeriod,
  type Climatology,
  type PeriodGrouping,
} from '../services/research/climatology'
import { latLonToTexelUv, texelUvToLatLon } from '../services/datasetProbe'
import { PeriodRun, stepFrames } from '../services/research/periodRun'
import type { LiveChangeView } from '../services/earthTileLayer'
import { buildChangeLegend, changeGradient } from './changeLegend'
import {
  baselineFit,
  gridKey,
  largestZonalChange,
  regionKey,
  statDeltas,
  withinResolution,
  zonalDifference,
  type Baseline,
  type BaselineFit,
  type BaselineStat,
} from '../services/research/baseline'
import { openPlans } from './xrPlansUI'
import { roundUncertainty } from '../services/errorBudget'
import { releaseColorScale } from '../services/dashRelease'
import { assistantActing, trail, trailOn } from '../services/research/researchTrail'
import { formatFrameTime } from '../services/research/citation'
import { precisionOfCadence } from '../services/research/frameTime'
import type { BundleCsv } from '../services/research/bundle'
import type { ChatBaselineState, ChatCompareResult } from '../ayni-chat/xrChatOutcomes'

/** AYNI: a number as the research record's summaries write it (5 significant figures, English). */
const recNum = (v: number | null | undefined): string => (typeof v === 'number' && Number.isFinite(v) ? String(Number(v.toPrecision(5))) : '—')
import { renderHovmollerSection, resetHovmoller, setHovmollerByAssistant, type HovmollerFeed, type HovmollerLine } from './hovmollerUI'
import type { MethodsBaseline, MethodsChange, MethodsCompare, MethodsLine, MethodsStatistics, LatLonBox } from '../services/research/methods'
import { renderThresholdSection, resetThreshold } from './thresholdUI'
import { renderRegionSeriesSection, resetRegionSeries } from './regionSeriesUI'
import { logCodeRatio, readingUncertainty, scaleCodeStep } from '../services/valueFidelity'

/**
 * AYNI: measuring is part of the plans with `data` (planAccess.ts). Without
 * one, the plans open with a line saying so, and nothing is measured.
 */
export function analysisAllowed(triggeredBy?: HTMLElement | null): boolean {
  if (dataToolsAllowed()) return true
  openPlans(triggeredBy ?? null, 'education', { notice: t('plans.locked.data') })
  return false
}

/**
 * AYNI (owner, 2026-10-06): Line and Box are not in Plus, which reads values
 * and pins; the plans open with a line saying where they are.
 */
function lineBoxAllowed(triggeredBy?: HTMLElement | null): boolean {
  if (!lineBoxLocked()) return true
  openPlans(triggeredBy ?? null, 'education', { notice: t('plans.locked.lineBox') })
  return false
}

/** What the panel needs from the app to compute anything. Injected so
 *  the module never reaches for a renderer singleton, and so the tests
 *  can drive it with a fixed frame. */
export interface AnalyzeSource {
  /** The current frame plus the metadata that makes it meaningful, or
   *  null when nothing analysable is loaded. */
  frame(): {
    snapshot: LumaSnapshot
    scale: DisplayColorScale
    options: DatasetOverlayOptions
  } | null
  /** The box currently on screen, for "what I can see". */
  visibleBounds(): LatLonBounds | null
  /** The active viewing transform, so the histogram is painted in the
   *  colours the globe is actually using. */
  display(): ColorScaleDisplay
  /** Title of the dataset being analysed, for the CSV header. */
  datasetTitle(): string | null
  /** AYNI: the provenance rows every CSV carries for the view on screen
   *  (`src/services/research/citation.ts`); optional for callers and
   *  tests that have no view to describe. */
  provenance?(): readonly (readonly (string | number | null)[])[] | null
  /** AYNI: open "Cite this view"; the panel offers the button only when given. */
  citeView?(trigger: HTMLElement): void
  /**
   * AYNI: draw the change from the held baseline on the globe (a global
   * equirectangular canvas, research tier), or take it off with null.
   * `frameKey` is the frame it describes: the globe hides it on any other.
   */
  showChange?(layer: { canvas: HTMLCanvasElement; frameKey: string | null } | null): void
  /** AYNI: which frame is on the primary globe, so a drawn change can tell it is out of date. */
  frameKey?(): string | null
  /**
   * AYNI: draw the change every frame on the graphics card (the baseline's
   * codes and the 256 x 256 change table), or stop with null. False when
   * this globe cannot, and the change is then drawn when playback stops.
   */
  liveChange?(view: LiveChangeView | null): boolean
  /**
   * AYNI: the globe `liveChange` draws on now (the main one). The table last
   * sent is remembered with its globe: when the main globe is another one,
   * the table is sent again rather than assumed to be there.
   */
  liveGlobe?(): unknown
  /** AYNI: the dataset's time axis of frames (a stream's), for a period baseline; null without one. */
  timeAxis?(): AnalyzeTimeAxis | null
  /** AYNI: the value of the frame on the globe at a place (the pointer's probe); null without data. */
  probeValue?(lat: number, lon: number): number | null
  /** AYNI: the real-time overlays that carry values, to compare the dataset with ("Compare", research tier). */
  compareCandidates?(): readonly CompareCandidate[]
  /** AYNI: the dataset's frame date and time step, to pair it with the overlay's; null without dates. */
  frameInstant?(): FrameInstant | null
  /** AYNI: the overlay being compared (hidden on the globe and sampled at the pins while it is), or null. */
  compareActive?(id: string | null): void
  /** AYNI: that overlay's values at a pin, on the pin's slots, in its own units. */
  compareSeriesAt?(pinId: string): MeasurementSeries | null
  /** AYNI: the frame clock and the line's codes, for the Line over time (Hovmöller, research tier slice 3). */
  hovmoller?(): HovmollerFeed | null
  /** AYNI: the provenance rows for an export spanning many frames (the view without one frame's time). */
  spanProvenance?(): readonly (readonly (string | number | null)[])[] | null
  /** AYNI: open the figure and methods dialog (research tier, §Next S4/S5) on one of its tabs; the panel offers the buttons only when given. */
  figure?(trigger: HTMLElement, tab: 'figure' | 'methods'): void
  /**
   * AYNI: the reproducibility bundle (research tier, §Next S8): one zip with
   * the citation, the view link, the methods, every CSV, the figure and the
   * record. Resolves with the saved file once downloaded; null when there is
   * no dataset to describe.
   */
  bundle?(trigger: HTMLElement): Promise<{ filename: string; files: number; sha256: string } | null>
  /** AYNI: the view link's query for the view on screen (Lab space keeps a held frame's view to open it again). */
  viewQuery?(): string
  /** Identity of that dataset, so the panel can tell when the globe
   *  underneath it has been replaced. Title is not enough — two rows
   *  can share one. */
  datasetId(): string | null
  /** Two-point picking on the globe, or null where there is no map to
   *  pick on. Optional so a caller can wire the statistics without the
   *  transect, and so the tests can drive the panel with no map. */
  transect?(): TransectPicker | null
  /** Drawing the analysed box on the globe. Same optionality, same
   *  reason. */
  regionOutline?(): RegionOutline | null
  /** Drawing isolines on the globe. Same optionality, same reason. */
  contours?(): ContourOverlay | null
  /** AYNI: two-corner box picking on the globe, for "Box" in the Data tab.
   *  Same optionality, same reason. */
  boxPicker?(): BoxPicker | null
  /**
   * The frame the globe is showing, as the label it displays.
   *
   * These datasets are animations — an 85-frame forecast for the shipped
   * rows — so every number on this panel is a claim about one instant,
   * and until now the panel never said which. Injected rather than read
   * from the DOM here so the panel keeps its one-way dependency, and
   * sourced from the same label Orbit's tools stamp on their results, so
   * the two cannot disagree about which frame was measured.
   */
  frameTime?(): string | null
  /**
   * Identity of the frame on the globe — not for display, only for
   * telling whether it changed.
   *
   * Separate from `frameTime()` on purpose. That one is the label a
   * human reads, and it is the wrong thing to compare against: it is
   * snapped to the dataset's display interval, so several video frames
   * share one label, and it is absent entirely for a dataset with no
   * start/end times. A watch built on it compares null to null forever
   * and never fires — silently, which is the failure mode `15a5926`
   * already taught this codebase to distrust.
   */
  frameId?(): string | null
}

/**
 * AYNI: a stream's time axis as a period baseline reads it (research tier,
 * S2): frame i at startMs + i × cadenceMs, the frame on the globe, and a
 * way to be told of every frame presented and to show one.
 */
export interface AnalyzeTimeAxis extends ClimateAxis {
  /** The frame on the globe now; null when there is none. */
  currentFrame(): number | null
  /** The frame a video time falls in (the one the graphics card holds). */
  frameAtVideoTime(seconds: number): number
  /** The frame whose presentation time (the video's frame callback) is this. */
  frameAtMediaTime(seconds: number): number
  /** Whether the publisher measured frame i (not filled, interpolated, estimated or missing: the .dsa availability). */
  usable?(frame: number): boolean
  /** Pause and show one frame; resolves once it is on the globe (false if it could not be). */
  showFrame(frame: number): Promise<boolean>
  /** Call back with each frame presented (playing, seeking or stepping); returns a stop function. */
  onFrame(fn: (frame: number) => void): () => void
}

/**
 * The globe's side of the contours: draw the isoline, and take it away.
 *
 * Same seam shape as `RegionOutline` and `TransectPicker`, for the same
 * reason — the panel computes geometry and never learns that MapLibre
 * exists, and a test can drive the contour section with no map.
 */
/** AYNI: a real-time overlay that carries values, as Compare reads it (main.ts `compareCandidates`). */
export interface CompareCandidate {
  readonly id: string
  readonly title: string
  readonly units?: string
  /** Classes, not amounts: nothing to subtract. */
  readonly classified: boolean
  /** False while the dataset's date is outside the overlay's. */
  visible(): boolean
  /** Its frame now (the map rectangle), or null until one is decoded. */
  frame(): CompareField | null
  /** Its value at a place now, in its own units; null without data there. */
  valueAt(lat: number, lon: number): number | null
  /** Its frame's date and time step; null without dates. */
  instant(): FrameInstant | null
  /** AYNI: a real-time overlay on this globe, or another globe on screen (2 or 4 globes); an overlay when absent. */
  readonly kind?: 'overlay' | 'globe'
  /** AYNI: for a globe, its number on screen (1 to 4). */
  readonly globe?: number
  /** AYNI: its frame as the graphics card takes it and where its map sits in it, for the live difference; null when it cannot be. */
  live?(): { source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement; crop: { u0: number; v0: number; us: number; vs: number } | null } | null
  /** AYNI: the provenance rows for its dataset at its own frame, for the CSV. */
  provenance?(): readonly (readonly (string | number | null)[])[] | null
  /** AYNI: for a globe, show the frame at this instant (by its own axis); false when it cannot. */
  seekTo?(ms: number): Promise<boolean>
}

export interface ContourOverlay {
  show(levels: ContourLevel[]): void
  clear(): void
}

/**
 * The globe's side of the region picker: show where the numbers came
 * from.
 *
 * "Alabama, 180,000 km² with data" is unfalsifiable on a globe that
 * gives no indication of which box was measured — the panel names a
 * region and the map says nothing back. This is the same seam shape as
 * `TransectPicker`, for the same reason: the panel should not know that
 * MapLibre exists.
 */
export interface RegionOutline {
  show(bounds: LatLonBounds): void
  /** AYNI (owner, 2026-10-18): a country's own outline, ring by ring; the box is drawn where this is missing. */
  showShape?(rings: { lat: number; lon: number }[][]): void
  clear(): void
}

/**
 * The globe's side of the transect: pick two points, keep them
 * draggable, draw the line between them.
 *
 * A seam rather than a direct call into the renderer, for the same
 * reason `AnalyzeSource` is one — the panel should not know that
 * MapLibre exists, and a test should be able to drive a transect
 * without a map.
 */
export interface TransectPicker {
  /** Arm picking. `onChange` fires when the pair completes, on every
   *  drag of an endpoint, and with null when cleared. */
  begin(onChange: (ends: TransectEndpoints | null) => void, path?: LinePath): void
  /** AYNI: put the line on the globe by its ends, as if picked (the chatbot's `hovmoller.summary`). */
  set?(ends: TransectEndpoints, onChange: (ends: TransectEndpoints | null) => void): void
  /** How many of the two points have been placed. */
  progress(): number
  clear(): void
}

/**
 * AYNI: the globe's side of the box tool — two corners, each draggable.
 * `onChange` fires when the second corner lands, after a corner is
 * dragged, and with null when cleared. The outline itself is
 * `RegionOutline`'s, drawn once the box is the scope.
 */
export interface BoxPicker {
  begin(onChange: (bounds: LatLonBounds | null) => void, onFirstCorner?: () => void): void
  /** How many of the two corners have been placed. */
  progress(): number
  /** Remove the corners and any pending pick, without calling back. */
  clear(): void
}

/** Which region the statistics cover. AYNI: `zone` is a latitude band
 *  from {@link ANALYZE_ZONES}; `box` is one drawn on the globe. */
export type AnalyzeScope =
  | { kind: 'dataset' }
  | { kind: 'view' }
  | { kind: 'named'; name: string }
  | { kind: 'zone'; id: AnalyzeZoneId }
  /** AYNI (owner, 2026-10-18): a country by its outline (`data/countries.ts`), by Natural Earth's ADM0_A3. */
  | { kind: 'country'; id: string }
  /** AYNI: `label` names a box someone else set (the chatbot); a drawn box has none. */
  | { kind: 'box'; bounds: LatLonBounds; label?: string }

/** AYNI: the latitude bands AYNI 2D offers first (luma-data-panel.ts STATS_REGIONS). */
export const ANALYZE_ZONES = {
  tropics: { n: 23.44, s: -23.44, w: -180, e: 180 },
  northern: { n: 90, s: 0, w: -180, e: 180 },
  southern: { n: 0, s: -90, w: -180, e: 180 },
  arctic: { n: 90, s: 66.56, w: -180, e: 180 },
  antarctic: { n: -66.56, s: -90, w: -180, e: 180 },
} as const satisfies Record<string, LatLonBounds>

export type AnalyzeZoneId = keyof typeof ANALYZE_ZONES

const ZONE_LABEL: Record<AnalyzeZoneId, Parameters<typeof t>[0]> = {
  tropics: 'analyze.zone.tropics',
  northern: 'analyze.zone.northern',
  southern: 'analyze.zone.southern',
  arctic: 'analyze.zone.arctic',
  antarctic: 'analyze.zone.antarctic',
}

let source: AnalyzeSource | null = null
/** AYNI: the box tool is waiting for corners. */
let boxArmed = false
let scope: AnalyzeScope = { kind: 'dataset' }
let lastResult: { stats: RegionStats; hist: LumaHistogram; scale: DisplayColorScale } | null = null
/**
 * The texel window the region statistics were computed over.
 *
 * Both surfaces below the statistics read it, for the same reason: the
 * zonal profile must describe the same box rather than silently widening
 * to the whole frame, and an isoline must cover exactly the region the
 * numbers beside it describe. They arrived as two variables holding one
 * value — `lastWindow` and `contourWindow` — which is a pair that can
 * only ever drift apart, so they are one.
 *
 * `undefined` is the whole dataset, which is what the reducers already
 * take to mean "no window". Only read when `lastResult` is set.
 */
let lastWindow: TexelWindow | undefined
let lastZonal: { samples: ZonalSample[]; scale: DisplayColorScale } | null = null
/** AYNI: the frame held as a baseline (research tier, `research/baseline.ts`).
 *  Outlives the panel and a dataset change, so another dataset on the same
 *  grid can be read against it; "Clear" drops it. */
let baseline: Baseline | null = null
/** AYNI: the region and texels the statistics on screen cover (`regionKey`). */
let lastRegionKey = ''
/** AYNI: the baseline's fit to the numbers on screen, or null when none applies. */
let lastFit: BaselineFit | null = null
/** AYNI: the frame on screen against a period's climatology, over the region on screen (S2, `anomalySummary`). */
let periodAnomalyNow: { summary: AnomalySummary; group: number } | null = null
/** AYNI: whether this refresh drew the change (else it is taken off at the end). */
let changeDrawn = false
/** AYNI: the change table last handed to the graphics card, so an unchanged one is not sent again. */
let liveKey: string | null = null
/** AYNI: true once this globe refused the live path; the change is then drawn when playback stops. */
let liveRefused = false
/** AYNI: the globe `liveKey`'s table was handed to (`AnalyzeSource.liveGlobe`). */
let liveTarget: unknown = null

/** AYNI: the live table `key` is already on the globe that draws now, not merely on some globe. */
function liveCurrent(src: AnalyzeSource, key: string): boolean {
  return key === liveKey && (src.liveGlobe?.() ?? null) === liveTarget
}

/** AYNI: remember the table just handed to the main globe. */
function liveSent(src: AnalyzeSource, key: string): void {
  liveKey = key
  liveTarget = src.liveGlobe?.() ?? null
}

/** AYNI: take the change off the globe, both ways it can be drawn, and tell the readers. */
function takeChangeOff(): void {
  source?.showChange?.(null)
  if (liveKey !== null) source?.liveChange?.(null)
  liveKey = null
  if (changeViewState().other) {
    source?.compareActive?.(null)
    setChangeView({ other: null, mode: null })
  }
  setChangeView({ shown: false })
}
/**
 * AYNI: the baseline can be one frame or a period (research tier, S2;
 * `research/climatology.ts`). Which the Baseline tab shows; whichever is
 * held is the baseline (holding one drops the other).
 */
type BaselineKind = 'frame' | 'period'
let baselineKind: BaselineKind = 'frame'
/** AYNI: the period the reader typed; null: the default for the dataset's axis. */
let periodChoice: ClimatePeriod | null = null

/** AYNI: a period being read, or read: the run, where it came from, and once complete the baseline it gives. */
interface PeriodState {
  readonly run: PeriodRun
  readonly datasetId: string
  readonly datasetTitle: string | null
  readonly calendar: ClimateCalendar
  readonly period: ClimatePeriod | null
  /** "1991–2020", or the window's dates. */
  readonly span: string
  readonly scale: DisplayColorScale
  readonly options: DatasetOverlayOptions
  readonly gridKey: string
  readonly units?: string
  stop: (() => void) | null
  stepping: boolean
  cancelStep: boolean
  /** Once complete: the climatology and the baseline the readers use. */
  clim: Climatology | null
  field: BaselineField | null
  /** AYNI: the chatbot started it, so the research record notes its completion as the chatbot's. */
  byAssistant?: boolean
}
let periodState: PeriodState | null = null
/** AYNI (Lab space): the view and region a held frame was measured on, for sharing its definition. */
let heldLab: { readonly for: Baseline; readonly query: string; readonly region: string } | null = null

/** AYNI: stop reading frames, and drop the period and its change. */
function clearPeriod(): void {
  if (!periodState) return
  periodState.stop?.()
  periodState.cancelStep = true
  const held = periodState.field
  periodState = null
  periodAnomalyNow = null
  if (held && changeViewState().field === held) setChangeView({ mode: null, field: null })
}

/** AYNI: "1991–2020", or the window's first and last dates. */
function periodSpan(axis: ClimateAxis, period: ClimatePeriod | null): string {
  if (period) return t('research.period.span', { from: String(period.from), to: String(period.to) })
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }
  return t('research.period.span', { from: formatDate(axis.startMs, opts), to: formatDate(climateFrameTimeMs(axis, axis.frameCount - 1), opts) })
}

/**
 * AYNI: the group of frame `frame` of the dataset on the globe, in the
 * climatology's calendar: by its index on the axis (`climatology.frameOf`);
 * null when the axis groups its frames another way (another cadence).
 */
function groupOfFrame(cal: ClimateCalendar, frame: number | null): number | null {
  const axis = source?.timeAxis?.() ?? null
  if (!axis || frame === null) return null
  const here = calendarFor(axis)
  if (here.grouping !== cal.grouping || here.groups !== cal.groups) return null
  return frameOf(axis, here, frame).group
}

/** AYNI: the group of the frame on the globe; null when it cannot be told. */
function groupNow(cal: ClimateCalendar): number | null {
  return groupOfFrame(cal, source?.timeAxis?.()?.currentFrame() ?? null)
}

/** AYNI: what a group stands for, in words ("January", "January, 03:00–06:00 UTC"); null for annual data or a window. */
function groupName(cal: ClimateCalendar, group: number, english = false): string | null {
  const parts = groupParts(cal, group)
  if (parts.month === null) return null
  const at = Date.UTC(2001, parts.month, 15)
  const month = english ? new Date(at).toLocaleString('en', { month: 'long', timeZone: 'UTC' }) : formatDate(at, { month: 'long', timeZone: 'UTC' })
  if (parts.fromHour === null || parts.toHour === null) return month
  const hh = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`
  return `${month}, ${hh(parts.fromHour)}–${hh(parts.toHour)} UTC` // i18n-exempt: a month name and UTC hours
}

/** AYNI: read the frame on the globe into the period, if it is one of its frames still to be read. */
function readPeriodFrame(frame: number): void {
  const state = periodState
  const src = source
  if (!state || !src || !state.run.wants(frame) || src.datasetId() !== state.datasetId) return
  const f = src.frame()
  if (!f) return
  state.run.accept(frame, {
    datasetId: src.datasetId(),
    gridKey: gridKey(f.snapshot.width, f.snapshot.height, f.options),
    codes: f.snapshot.data,
    width: f.snapshot.width,
    height: f.snapshot.height,
  })
  showPeriodProgress()
  if (state.run.complete()) finishPeriod()
}

/** AYNI: "214 of 360 frames read", in place, without recomputing the panel; "Use the frames read" once 80 % are in. */
function showPeriodProgress(): void {
  const state = periodState
  if (!state || !root) return
  const text = root.querySelector<HTMLElement>('.analyze-period-progress')
  if (text) text.textContent = t('research.period.progress', { read: formatNumber(state.run.read), total: formatNumber(state.run.total) })
  const bar = root.querySelector<HTMLProgressElement>('.analyze-period-bar')
  if (bar) bar.value = state.run.read
  const early = root.querySelector<HTMLButtonElement>('.analyze-period-early')
  if (early) {
    early.hidden = !state.run.enough()
    early.textContent = t('research.period.useEarly', { read: formatNumber(state.run.read) })
  }
}

/** AYNI: every frame read (or, asked, enough): the climatology becomes the baseline the readers and the globe use. */
function finishPeriod(early = false): void {
  const state = periodState
  if (!state || state.clim) return
  const clim = state.run.climatology(early)
  if (!clim) return
  state.stop?.()
  state.stop = null
  state.cancelStep = true
  state.clim = clim
  const cal = state.calendar
  state.field = {
    data: new Uint8Array(0),
    width: clim.width,
    height: clim.height,
    scale: state.scale,
    options: state.options,
    gridKey: state.gridKey,
    frameLabel: t('research.period.legendLabel', { span: state.span }),
    units: state.units,
    period: {
      clim,
      label: state.span,
      groupAt: (ms) => {
        const axis = source?.timeAxis?.() ?? null
        return axis ? groupOfFrame(cal, frameAtTime(axis, ms)) : null
      },
      groupNow: () => groupNow(cal),
      // A thinned climatology: the frame read at the texel the cell was read at.
      valueAtCell: (lat, lon) => {
        const uv = latLonToTexelUv(lat, lon, state.options)
        if (!uv) return null
        const x = Math.min(clim.width - 1, Math.max(0, Math.floor(uv.u * clim.width)))
        const y = Math.min(clim.height - 1, Math.max(0, Math.floor(uv.v * clim.height)))
        const at = sampledTexel(clim, x, y)
        const place = texelUvToLatLon({ u: (at.x + 0.5) / clim.width, v: (at.y + 0.5) / clim.height }, state.options)
        return source?.probeValue?.(place.lat, place.lon) ?? null
      },
    },
  }
  liveRefused = false
  setChangeView({ field: state.field })
  // AYNI: a research record, when one is recording, notes the period's definition (slice 4).
  const framesRead = clim.framesPerGroup.reduce((a, b) => a + b, 0)
  const framesExpected = clim.expectedPerGroup.reduce((a, b) => a + b, 0)
  trail({
    type: 'baseline',
    ...(state.byAssistant ? { actor: 'assistant' as const } : {}),
    data: {
      kind: 'period',
      dataset: state.datasetId,
      datasetTitle: state.datasetTitle,
      span: state.span,
      ...(state.period ? { period: state.period } : {}),
      grouping: cal.grouping,
      groups: clim.groups,
      framesRead,
      framesExpected,
      yearsPerGroup: clim.yearsPerGroup,
      sigma: clim.hasSigma,
      flooredCells: clim.flooredCells,
      cellsWithSigma: clim.cellsWithSigma,
      thinning: clim.factor,
      early,
      units: state.units ?? '',
      method: periodMethodText(cal.grouping),
      summary: `Period ${state.span} (${cal.grouping}): ${framesRead} of ${framesExpected} frames read${clim.hasSigma ? ', with σ' : ''}`,
    },
  })
  const host = root?.querySelector('.analyze-body') as HTMLElement | null
  if (host) {
    refresh(host)
    ;(host.querySelector('.analyze-baseline-clear') as HTMLElement | null)?.focus()
  }
}

/** AYNI: start reading a period of the dataset on the globe (dropping a held frame); false when it cannot. */
function startPeriod(src: AnalyzeSource, axis: AnalyzeTimeAxis, period: ClimatePeriod | null, measuredScale: DisplayColorScale): boolean {
  const frame = lastFrame
  const datasetId = src.datasetId()
  if (!frame || !datasetId) return false
  const run = new PeriodRun({
    datasetId,
    datasetTitle: src.datasetTitle(),
    units: measuredScale.units,
    gridKey: gridKey(frame.snapshot.width, frame.snapshot.height, frame.options),
    width: frame.snapshot.width,
    height: frame.snapshot.height,
    values: valueTable(frame.scale),
    axis: { startMs: axis.startMs, frameCount: axis.frameCount, cadenceMs: axis.cadenceMs },
    period,
    usable: axis.usable,
  })
  if (run.check.refusal || !run.scale) return false
  clearPeriod()
  baseline = null
  setChangeView({ mode: null, field: null })
  periodState = {
    run,
    datasetId,
    datasetTitle: src.datasetTitle(),
    calendar: run.calendar,
    period,
    span: periodSpan(axis, period),
    scale: frame.scale,
    options: frame.options,
    gridKey: gridKey(frame.snapshot.width, frame.snapshot.height, frame.options),
    units: measuredScale.units,
    stop: null,
    stepping: false,
    cancelStep: false,
    clim: null,
    field: null,
  }
  periodState.stop = axis.onFrame((i) => readPeriodFrame(i))
  // The frame on the globe now counts too.
  const now = axis.currentFrame()
  if (now !== null) readPeriodFrame(now)
  return true
}

/** AYNI: show each frame still missing, one after another (playback paused), and read it. */
async function stepPeriod(axis: AnalyzeTimeAxis): Promise<void> {
  const state = periodState
  if (!state || state.stepping || state.clim) return
  state.stepping = true
  state.cancelStep = false
  rerenderPanel('.analyze-period-stop')
  await stepFrames(state.run.missing(), (frame) => axis.showFrame(frame), readPeriodFrame, () => state.cancelStep || periodState !== state)
  state.stepping = false
  if (periodState === state && !state.clim) rerenderPanel('.analyze-period-step')
}

/**
 * AYNI: the frame on screen against the climatology of its group over the
 * region on screen, place by place where both have data (`anomalySummary`);
 * null when the frame's group cannot be told (nothing is compared then).
 */
function periodAnomaly(frame: { snapshot: LumaSnapshot; scale: DisplayColorScale; options: DatasetOverlayOptions }, window: TexelWindow | undefined): { summary: AnomalySummary; group: number } | null {
  const state = periodState
  if (!state?.clim) return null
  const group = groupNow(state.calendar)
  if (group === null) return null
  const summary = anomalySummary(frame.snapshot, valueTable(frame.scale), state.clim, group, frame.options, window)
  return summary ? { summary, group } : null
}

/** AYNI: whether the period's climatology can be read against the frame on screen. */
function periodFit(units: string | undefined, frame: { snapshot: LumaSnapshot; options: DatasetOverlayOptions }): BaselineFit {
  const state = periodState!
  if ((state.units ?? '') !== (units ?? '')) return 'other-units'
  if (state.gridKey !== gridKey(frame.snapshot.width, frame.snapshot.height, frame.options)) return 'other-grid'
  return groupNow(state.calendar) === null ? 'other-calendar' : 'applies'
}

/** AYNI: the method in words, for the panel. */
function periodMethod(grouping: PeriodGrouping): string {
  if (grouping === 'window') return t('research.period.methodWindow')
  const spread = grouping === 'daily' ? t('research.period.spreadDaily') : grouping === 'subdaily' ? t('research.period.spreadSubdaily') : t('research.period.spreadYearly')
  return t('research.period.method', { min: String(MIN_SIGMA_YEARS), spread })
}

let lastTrigger: HTMLElement | null = null
let root: HTMLElement | null = null
/** The dataset the numbers on screen were computed from. */
let openedFor: string | null = null
/** The transect section's own container, so a drag can redraw it
 *  without recomputing the region statistics above it. */
/**
 * AYNI (owner, 2026-10-06): the panel's tabs, as in Tools, so it is not one
 * long column. The region picker above them applies to all but Profiles' line.
 */
export type AnalyzeTab = 'stats' | 'profiles' | 'contours' | 'baseline' | 'compare'
const ANALYZE_TABS: readonly AnalyzeTab[] = ['stats', 'profiles', 'contours', 'baseline', 'compare']
const ANALYZE_TAB_LABEL: Record<AnalyzeTab, MessageKey> = {
  stats: 'analyze.tab.stats',
  profiles: 'analyze.tab.profiles',
  contours: 'analyze.tab.contours',
  baseline: 'analyze.tab.baseline',
  compare: 'analyze.tab.compare',
}
const ANALYZE_TAB_KEY = 'ayni-analyze-tab'
let activeTab: AnalyzeTab = (() => {
  try {
    const v = localStorage.getItem(ANALYZE_TAB_KEY) as AnalyzeTab | null
    return v && ANALYZE_TABS.includes(v) ? v : 'stats'
  } catch {
    return 'stats'
  }
})()

/** AYNI: show one of the panel's tabs (now, if it is open, else when it opens), and remember it. */
export function setAnalyzeTab(tab: AnalyzeTab, focus = false): void {
  activeTab = tab
  if (tab === 'compare' && root) compareSeen = true
  try {
    localStorage.setItem(ANALYZE_TAB_KEY, tab)
  } catch {
    // storage refused: the next open starts on Statistics
  }
  if (!root) return
  for (const id of ANALYZE_TABS) {
    const btn = root.querySelector<HTMLElement>(`#analyze-tab-${id}`)
    const panel = root.querySelector<HTMLElement>(`#analyze-tabpanel-${id}`)
    const on = id === tab
    btn?.setAttribute('aria-selected', String(on))
    btn?.setAttribute('tabindex', on ? '0' : '-1')
    btn?.classList.toggle('active', on)
    if (panel) panel.hidden = !on
    if (on && focus) btn?.focus()
  }
}

/** The current tab, for tests. */
export function analyzeTab(): AnalyzeTab {
  return activeTab
}

/** The tab list and one panel per tab, in `body`; tabs in `missing` are left out. */
function buildTabs(body: HTMLElement, missing: ReadonlySet<AnalyzeTab>): Record<AnalyzeTab, HTMLElement> {
  const list = document.createElement('div')
  list.className = 'analyze-tabs'
  list.setAttribute('role', 'tablist')
  list.setAttribute('aria-label', t('analyze.tabs.aria'))
  body.appendChild(list)
  const shown = ANALYZE_TABS.filter((id) => !missing.has(id))
  if (!shown.includes(activeTab)) activeTab = 'stats'
  const panels = {} as Record<AnalyzeTab, HTMLElement>
  for (const id of ANALYZE_TABS) {
    const panel = document.createElement('div')
    panel.className = 'analyze-tabpanel'
    panel.id = `analyze-tabpanel-${id}`
    panel.setAttribute('role', 'tabpanel')
    panel.setAttribute('aria-labelledby', `analyze-tab-${id}`)
    panel.hidden = id !== activeTab
    panels[id] = panel
    if (missing.has(id)) continue
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'analyze-tab'
    btn.id = `analyze-tab-${id}`
    btn.setAttribute('role', 'tab')
    btn.setAttribute('aria-controls', panel.id)
    btn.setAttribute('aria-selected', String(id === activeTab))
    btn.tabIndex = id === activeTab ? 0 : -1
    btn.classList.toggle('active', id === activeTab)
    btn.textContent = t(ANALYZE_TAB_LABEL[id])
    btn.addEventListener('click', () => setAnalyzeTab(id))
    btn.addEventListener('keydown', (ev) => {
      const i = shown.indexOf(id)
      const last = shown.length - 1
      const to = ev.key === 'ArrowRight' ? (i === last ? 0 : i + 1)
        : ev.key === 'ArrowLeft' ? (i === 0 ? last : i - 1)
          : ev.key === 'Home' ? 0 : ev.key === 'End' ? last : -1
      if (to < 0) return
      ev.preventDefault()
      setAnalyzeTab(shown[to], true)
    })
    list.appendChild(btn)
    body.appendChild(panel)
  }
  return panels
}

/** AYNI: Compare's choice: which overlay, whether its difference is on the globe, and the fixed colour range. */
let compareId: string | null = null
/** AYNI: what Compare draws on the globe: nothing, A − B, or the difference as a % of B (ratio quantities). */
type CompareMode = 'difference' | 'percent'
let compareMode: CompareMode | null = null
let compareLimit: { key: string; limit: number; floor: number | null } | null = null
/** AYNI (owner, 2026-10-20): Compare's own colour range, for one pair (B, its conversion, the mode); null for ±limit. */
let compareRange: { key: string; range: ChangeRange } | null = null
/** AYNI (owner, 2026-10-20): the change map's colour dialog, kept open across the panel's redraws. */
let changeDialog: PaletteDialogHandle | null = null
/** AYNI: where B comes from (null: an overlay if there is one, else another globe), and how frames are paired. */
type CompareSource = 'overlay' | 'globe'
let compareSource: CompareSource | null = null
let comparePairing: FramePairing = 'time'
/** AYNI: B's frame last handed to the graphics card, so a new element is handed over again. */
let liveCompareSource: unknown = null
/** AYNI: what Compare computed on the last refresh, for the methods paragraph; null when nothing was. */
let lastCompare: MethodsCompare | null = null
/** AYNI: the Compare tab was looked at (its numbers count as used, for the methods paragraph). */
let compareSeen = false
/**
 * AYNI (security review L6, 2026-10-07): what the chatbot set up, so the
 * numbers the panel notes later (a re-render when playback settles, a
 * comparison waiting for its other frame) stay the chatbot's in a research
 * record, until the person changes it.
 */
const chatOwned = { scope: false, compare: false }
const actorOf = (owned: boolean) => (owned ? { actor: 'assistant' as const } : {})
/** AYNI (the chatbot's compare.run): what Compare found on its last render, or why it refused. */
let compareChat: { ok: true; result: ChatCompareResult } | { ok: false; reason: string } | null = null
/** AYNI: the chatbot asked for % of B; a refusal says why rather than quietly showing A − B. */
let compareAskedPercent = false

let transectHost: HTMLElement | null = null
/** AYNI: the Line over time's section (research tier, slice 3), and the line it follows. */
let hovHost: HTMLElement | null = null
let hovLine: HovmollerLine | null = null
let transectEnds: TransectEndpoints | null = null
let transectArmed = false
/** The contour section's own container, so changing the threshold
 *  redraws the isoline without recomputing the histogram above it. */
let contourHost: HTMLElement | null = null
/** Whether lines are currently on the globe, so the section can offer
 *  Draw or Clear rather than inferring it from the threshold. */
let contourDrawn = false
/** Identity of the frame the drawn contours were computed from, as
 *  reported by `frameId()`. Compared, never displayed. */
let contourFrameId: string | null = null
/** Interval handle for the staleness watch, live only while lines are up. */
let contourWatch: number | null = null
/** Set when the watch removed the lines, so the section can say why they
 *  went rather than leaving the user to wonder. Cleared on the next draw. */
let contourStaleCleared = false
/** Set when a settle should put the lines back. Survives the `refresh`
 *  that clears every other contour flag, because it records what the
 *  viewer asked for rather than what is currently on the globe. */
let contourRedrawPending = false
/**
 * How many isolines to aim for.
 *
 * Passed to `colorbarTicks`, which rounds to a 1/2/5 × 10^k step and so
 * returns near this rather than exactly it. Five is a starting point,
 * not a measurement: on a smoke plume at low zoom, much past a dozen
 * lines reads as hatching rather than as structure, and the right
 * number is the sort of thing that only settles by looking at real
 * fields.
 */
const CONTOUR_LEVEL_TARGET = 5
let lastTransect: { samples: TransectSample[]; scale: DisplayColorScale } | null = null
/**
 * The frame everything on screen was computed from.
 *
 * Held so a drag re-samples the transect against the same frame the
 * histogram describes, rather than calling `frame()` per drag event: on
 * a playing video that would be a full readback at pointer rate, which
 * is the failure `glLumaSampler`'s docstring is emphatic about. It also
 * makes the panel internally consistent — one frame, one set of numbers.
 */
let lastFrame: {
  snapshot: LumaSnapshot
  scale: DisplayColorScale
  options: DatasetOverlayOptions
} | null = null

/**
 * Wire the panel to the app. Call once at boot.
 *
 * Resets the picked region, because that choice belongs to the source
 * it was made against. Within one session the choice is deliberately
 * sticky across opens — someone comparing regions should not have to
 * re-pick on every open — but a new source is a new context.
 */
export function initAnalyzeUI(src: AnalyzeSource): void {
  source?.transect?.()?.clear()
  source?.regionOutline?.()?.clear()
  source?.boxPicker?.()?.clear()
  clearContours()
  source = src
  scope = { kind: 'dataset' }
  transectEnds = null
  transectArmed = false
  boxArmed = false
}

/**
 * AYNI: "Line" in the Data tab — open the panel (if it is not) and start
 * picking the line's two ends; the profile appears in the panel.
 */
export function startAnalyzeLine(triggeredBy?: HTMLElement | null): void {
  if (!analysisAllowed(triggeredBy) || !lineBoxAllowed(triggeredBy)) return
  setAnalyzeTab('profiles')
  if (!root) openAnalyzeUI(triggeredBy)
  const picker = source?.transect?.()
  if (!picker || !transectHost) return
  clearTransect()
  transectArmed = true
  picker.begin((ends) => {
    transectEnds = ends
    transectArmed = false
    renderTransectSection()
  })
  renderTransectSection()
  transectHost.scrollIntoView?.({ block: 'nearest' })
}

/**
 * AYNI: "Box" in the Data tab — open the panel and pick two opposite
 * corners on the globe; the statistics then cover that box, outlined.
 */
export function startAnalyzeBox(triggeredBy?: HTMLElement | null): void {
  if (!analysisAllowed(triggeredBy) || !lineBoxAllowed(triggeredBy)) return
  setAnalyzeTab('stats')
  if (!root) openAnalyzeUI(triggeredBy)
  const picker = source?.boxPicker?.()
  if (!picker) return
  boxArmed = true
  picker.begin((bounds) => {
    boxArmed = false
    scope = bounds ? { kind: 'box', bounds } : { kind: 'dataset' }
    chatOwned.scope = false
    rebuildScopePicker()
    const body = root?.querySelector('.analyze-body') as HTMLElement | null
    if (body) refresh(body)
  }, rebuildScopePicker)
  rebuildScopePicker()
}

/** AYNI: open on a region (a zone, a named region, or the whole dataset), for the Data tab's statistics. */
export function openAnalyzeScoped(next: AnalyzeScope, triggeredBy?: HTMLElement | null): void {
  if (!analysisAllowed(triggeredBy)) return
  // AYNI (owner, 2026-10-05): statistics over a whole region are not in
  // Education; a drawn box is.
  if (next.kind !== 'box' && regionStatsLocked()) {
    openPlans(triggeredBy ?? null, 'research', { notice: t('plans.locked.regionStats') })
    return
  }
  if (next.kind === 'box' && !lineBoxAllowed(triggeredBy)) return
  openAnalyzeUI(triggeredBy, next)
}

/**
 * AYNI: take every analysis annotation off the globe — line, box, outline,
 * isolines — and close the panel. The Data tab's "Clear all".
 */
export function clearAnalysis(): void {
  baseline = null
  // AYNI: a period too, read or being read, and the years typed.
  clearPeriod()
  periodChoice = null
  liveRefused = false
  takeChangeOff()
  setChangeView({ mode: null, field: null })
  source?.boxPicker?.()?.clear()
  boxArmed = false
  if (scope.kind === 'box') scope = { kind: 'dataset' }
  if (root) closeAnalyzeUI()
  else {
    clearTransect()
    clearContours()
    source?.regionOutline?.()?.clear()
  }
}

/** Whether the panel is currently open. Used by tests and the scene. */
export function isAnalyzeUIOpen(): boolean {
  return root !== null
}

/** Close the panel and return focus to whatever opened it. */
export function closeAnalyzeUI(): void {
  closePanel(true)
}

/**
 * AYNI (owner, 2026-10-06): Analyze is a side panel like Browse and Tools:
 * it slides in and out on their time and curve, and the globe and the
 * corner buttons step aside for it (`globePanelOffset.ts`, told by this
 * event when it opens or closes).
 */
export const SIDE_PANEL_EVENT = 'ayni-side-panel'
function announceSidePanel(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SIDE_PANEL_EVENT))
}

/** Slide a closed panel out, then remove it; at once without motion, or when it is being replaced. */
function leave(el: HTMLElement, animate: boolean): void {
  const still = typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!animate || still) {
    el.remove()
    return
  }
  el.classList.add('analyze-leaving')
  el.setAttribute('aria-hidden', 'true')
  el.inert = true
  window.setTimeout(() => el.remove(), 700)
}

function closePanel(animate: boolean): void {
  // The line and the region box are drawn on the globe, not in the
  // panel, so closing has to take them with it — either left behind
  // would be an annotation with nothing on screen left to explain or
  // remove it.
  clearTransect()
  clearContours()
  stopLayoutWatch()
  source?.regionOutline?.()?.clear()
  // AYNI: the change map goes with the panel that explains it; reopening redraws it.
  takeChangeOff()
  changeDialog?.close()
  // AYNI: the box's corners go with it (silently: `clear` does not call
  // back). The box stays the scope, so reopening measures it again.
  source?.boxPicker?.()?.clear()
  boxArmed = false
  const was = root
  root = null
  if (was) leave(was, animate)
  openedFor = null
  transectHost = null
  hovHost = null
  contourHost = null
  lastFrame = null
  lastCompare = null
  compareCsv = null
  bundleStatus = ''
  compareSeen = false
  // AYNI: the area over a threshold and the region through time stop recording with the panel.
  resetThreshold()
  resetRegionSeries()
  document.removeEventListener('keydown', onEscape, true)
  lastTrigger?.focus()
  lastTrigger = null
  if (was) announceSidePanel()
}

function clearTransect(): void {
  source?.transect?.()?.clear()
  transectEnds = null
  transectArmed = false
  lastTransect = null
  // AYNI: the Line over time goes with its line (and the chatbot's claim on it, L6).
  hovLine = null
  resetHovmoller()
  setHovmollerByAssistant(false)
}

/**
 * Close the panel when the globe under it changes to a different
 * dataset.
 *
 * The numbers are computed once, on open, against one frame of one
 * dataset. Swapping the dataset while the panel stays open leaves a
 * statistics table describing something that is no longer on screen —
 * every figure real, every figure about the wrong thing, and nothing
 * saying so. That is precisely the failure this module's own docstring
 * names, and it survived because the existing teardown only fired when
 * *no* panel had a data-encoded dataset left; swapping one for another
 * kept it open.
 *
 * Closed rather than recomputed on purpose. Recomputing would spend a
 * full-frame readback the viewer did not ask for, and the region they
 * picked belonged to the dataset that just left.
 */
export function notifyAnalyzeDatasetChanged(datasetId: string | null): void {
  if (root && openedFor !== datasetId) closeAnalyzeUI()
}

/**
 * Recompute against the frame the globe has settled on.
 *
 * The host calls this when playback pauses or a seek finishes — see
 * `services/playbackSettle`, which decides *when* that is and is
 * deliberately silent during playback at any rate. Until now the panel
 * computed once, on open, and everything on it then described whichever
 * frame happened to be up at that moment for as long as it stayed open.
 * It said which frame, so it was honest, but it went quietly out of date
 * the moment anyone touched the transport.
 *
 * `refresh` is the whole of it because `refresh` was already the "the
 * frame or the region moved, do it all again" path — the same one the
 * scope picker uses. `npm run bench:analysis` is what makes that
 * affordable: histogram, summary and zonal profile together are ~60 ms
 * on a 4096×2048 frame, which on a settle is a hitch nobody perceives,
 * with no worker and no async readback.
 *
 * **Contours come back too, but only if they were up.** They are the
 * expensive part — 178–376 ms depending on how much perimeter the levels
 * cut, against ~60 ms for everything else — so they are redrawn on the
 * strength of the viewer having asked for them, never speculatively. A
 * pause with no lines on the globe costs nothing extra.
 *
 * That intent has to be read *before* `refresh`, which clears every
 * contour flag by design: anything drawn was extracted against the
 * previous frame and cannot survive a recompute. `contourDrawn` covers a
 * pause quick enough that the staleness watch has not fired yet;
 * `contourStaleCleared` covers the ordinary case where it has, and the
 * lines are already down with the panel explaining why. Either means the
 * same thing here — the viewer wanted contours, and there is now a
 * settled frame to draw them against.
 */
export function notifyAnalyzePlaybackSettled(): void {
  const body = root?.querySelector('.analyze-body') as HTMLElement | null
  if (!body) return
  contourRedrawPending = contourDrawn || contourStaleCleared
  refresh(body)
}

/** Gap between the panel's bottom edge and whatever it is clearing. */
const PANEL_CLEARANCE_GAP = 12

/**
 * Keep the panel clear of the playback transport and the Tools bar.
 *
 * All three want the same bottom-end corner — the panel's own header
 * comment says it deliberately shares it with the colorbar controls,
 * which is fine because those two are never open together. The
 * transport is not like that. It is open exactly when the panel is most
 * useful, the panel is `z-index: 60` and wins, and closing the panel to
 * reach Play takes the contours with it (`closeAnalyzeUI` clears them,
 * so the annotation cannot outlive its explanation). The result was no
 * pointer-only way to play a dataset while looking at its analysis.
 *
 * `mapControlsUI.updateMapControlsPosition` already solves this for the
 * Tools bar by measuring the transport, so the shape here follows it.
 * Both are measured rather than only the Tools bar: that would work
 * today, since the bar is kept above the transport, but it would make
 * this quietly depend on an invariant maintained in another module.
 *
 * `max-block-size` shrinks by the same amount. Without it a tall panel
 * on a short window keeps its 34rem and simply grows off the top.
 */
function positionAnalyzePanel(): void {
  const panel = root
  if (!panel) return

  let clearance = 0
  for (const id of ['playback-controls', 'map-controls']) {
    const el = document.getElementById(id)
    if (!el || el.classList.contains('hidden')) continue
    const rect = el.getBoundingClientRect()
    // Zero-sized means display:none by some other route; ignore it
    // rather than clearing a strip of nothing.
    if (rect.height <= 0) continue
    clearance = Math.max(clearance, window.innerHeight - rect.top)
  }

  if (clearance <= 0) {
    // Nothing to clear — hand the corner back to the stylesheet rather
    // than pinning an inline value it would then have to fight.
    panel.style.insetBlockEnd = ''
    panel.style.maxBlockSize = ''
    return
  }
  const offset = clearance + PANEL_CLEARANCE_GAP
  panel.style.insetBlockEnd = `${offset}px`
  // AYNI (owner, 2026-10-06): wider than a phone, the panel is as tall as
  // the Tools popover: analyze.css pins its head under the corner buttons
  // and this foot sets the rest, so no cap.
  panel.style.maxBlockSize = window.innerWidth > 600 ? '' : `min(34rem, calc(100vh - ${offset}px - 1rem))`
}

/** Watches what the panel has to stay clear of, for as long as it is
 *  open. A dataset loading mounts the transport *after* the panel may
 *  already be up, and toggling `display:none` reports as a resize to
 *  zero, so one observer covers both appearing and vanishing. */
let layoutWatch: ResizeObserver | null = null

function startLayoutWatch(): void {
  stopLayoutWatch()
  window.addEventListener('resize', positionAnalyzePanel)
  if (typeof ResizeObserver === 'undefined') return
  layoutWatch = new ResizeObserver(() => positionAnalyzePanel())
  for (const id of ['playback-controls', 'map-controls']) {
    const el = document.getElementById(id)
    if (el) layoutWatch.observe(el)
  }
}

function stopLayoutWatch(): void {
  window.removeEventListener('resize', positionAnalyzePanel)
  layoutWatch?.disconnect()
  layoutWatch = null
}

function onEscape(ev: KeyboardEvent): void {
  // AYNI (2026-10-20): a palette dialog open over the panel takes its own Escape first.
  if (ev.key === 'Escape' && currentPaletteDialog()) return
  if (ev.key === 'Escape') {
    ev.stopPropagation()
    closeAnalyzeUI()
  }
}

/**
 * Open the panel and compute against the current frame.
 *
 * Rebuilt from scratch on open rather than cached: the frame moves, the
 * palette moves, and a panel showing last week's numbers under this
 * week's globe is worse than no panel.
 */
export function openAnalyzeUI(
  triggeredBy?: HTMLElement | null,
  /** §A6 — open already scoped to a region, for the chip on an Orbit
   *  message that just described one. Overrides the sticky choice from
   *  the last open, because arriving here from "the smoke is worst over
   *  Colorado" and landing on the previous region would be the one
   *  wrong answer available. */
  presetScope?: AnalyzeScope,
): HTMLElement {
  // AYNI: a panel being replaced goes at once, as does one still sliding out.
  closePanel(false)
  if (typeof document !== 'undefined') document.querySelectorAll('.analyze-leaving').forEach((el) => el.remove())
  lastTrigger = triggeredBy ?? null
  if (presetScope) {
    scope = presetScope
    chatOwned.scope = assistantActing()
  }

  root = document.createElement('div')
  root.className = 'analyze-panel'
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'false')
  root.setAttribute('aria-label', t('analyze.title'))

  const header = document.createElement('div')
  header.className = 'analyze-header'
  const heading = document.createElement('h2')
  heading.textContent = t('analyze.title')
  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'analyze-close'
  close.setAttribute('aria-label', t('analyze.close'))
  close.textContent = '×' // i18n-exempt: a glyph, not a word
  close.addEventListener('click', closeAnalyzeUI)
  header.append(heading, close)
  root.appendChild(header)

  root.appendChild(buildScopePicker())

  const body = document.createElement('div')
  body.className = 'analyze-body'
  root.appendChild(body)

  document.body.appendChild(root)
  document.addEventListener('keydown', onEscape, true)
  positionAnalyzePanel()
  startLayoutWatch()
  openedFor = source?.datasetId() ?? null
  refresh(body)
  announceSidePanel()
  return root
}

function buildScopePicker(): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'analyze-scope'
  // AYNI: without full analysis the only region is a drawn box, so there
  // is nothing to pick until one exists (owner, 2026-10-05).
  const boxOnly = regionStatsLocked()
  if (boxOnly && scope.kind !== 'box') {
    if (boxArmed) wrap.appendChild(boxHint())
    return wrap
  }

  const label = document.createElement('label')
  label.className = 'analyze-scope-label'
  label.htmlFor = 'analyze-scope-select'
  label.textContent = t('analyze.scope.label')

  const select = document.createElement('select')
  select.id = 'analyze-scope-select'
  select.className = 'analyze-scope-select'

  let group: HTMLElement = select
  const add = (value: string, text: string): void => {
    const opt = document.createElement('option')
    opt.value = value
    opt.textContent = text
    group.appendChild(opt)
  }
  const startGroup = (label: string): void => {
    const g = document.createElement('optgroup')
    g.label = label
    select.appendChild(g)
    group = g
  }
  if (boxOnly) {
    add('box', scope.kind === 'box' && scope.label ? scope.label : t('analyze.scope.box'))
    select.value = 'box'
    wrap.append(label, select)
    if (boxArmed) wrap.appendChild(boxHint())
    return wrap
  }
  // AYNI: the whole globe and the latitude bands first, as AYNI 2D lists
  // them; then what is on screen and a drawn box; then named regions.
  startGroup(t('analyze.scope.group.globe'))
  add('dataset', t('analyze.scope.dataset'))
  for (const id of Object.keys(ANALYZE_ZONES) as AnalyzeZoneId[]) add(`zone:${id}`, t(ZONE_LABEL[id]))
  startGroup(t('analyze.scope.group.yours'))
  add('view', t('analyze.scope.view'))
  if (scope.kind === 'box') add('box', scope.label ?? t('analyze.scope.box'))
  // AYNI (owner, 2026-10-18): countries by their outline, in the reader's
  // language and order, once the outlines have loaded; the boxes of the
  // same countries leave the Regions group then, so one name means one region.
  const list = loadedCountries()
  const countryNames = new Set<string>()
  if (list) {
    for (const c of list) for (const n of [c.en, c.es, countryName(c, 'en')]) countryNames.add(n.toLowerCase())
  }
  startGroup(t('analyze.scope.group.regions'))
  // Named regions come from the same table the LLM's `<<REGION:…>>`
  // marker resolves against, so "Europe" means the same box wherever a
  // user meets it.
  //
  // Filtered by whether the display name resolves back to a box:
  // `getRegionNames` returns display names while `resolveRegion` looks
  // up lowercased *aliases*, and at least one entry's display name is
  // not among its own aliases. Offering it produced a region that
  // silently fell through to the whole dataset — the worst outcome
  // available, since the numbers were real and the label was wrong.
  for (const name of getRegionNames()) {
    if (!resolveRegion(name)) continue
    // The box of a country with an outline would be a second, rougher "Spain".
    if (countryNames.has(name.toLowerCase()) && !(scope.kind === 'named' && scope.name === name)) continue
    add(`named:${name}`, name)
  }
  if (list) {
    startGroup(t('analyze.scope.group.countries'))
    const lang = getLocale()
    const collator = new Intl.Collator(lang)
    const named = list.map((c) => ({ id: c.id, name: countryName(c, lang) })).sort((a, b) => collator.compare(a.name, b.name))
    for (const c of named) add(`country:${c.id}`, c.name)
  } else {
    void loadCountries().then((got) => {
      if (got) rebuildScopePicker()
    })
  }

  select.value = scopeToValue(scope)
  select.addEventListener('change', () => {
    scope = valueToScope(select.value)
    chatOwned.scope = false
    const body = root?.querySelector('.analyze-body') as HTMLElement | null
    if (body) refresh(body)
  })

  wrap.append(label, select)
  // AYNI: what the box tool is waiting for, while it waits.
  if (boxArmed) wrap.appendChild(boxHint())
  return wrap
}

/** AYNI: what the box tool is waiting for, while it waits. */
function boxHint(): HTMLElement {
  const hint = message(
    (source?.boxPicker?.()?.progress() ?? 0) >= 1 ? t('analyze.box.pickSecond') : t('analyze.box.pickFirst'),
  )
  hint.classList.add('analyze-box-hint')
  return hint
}

/** AYNI: redraw the scope picker in place (a box was added or the box tool armed). */
function rebuildScopePicker(): void {
  const old = root?.querySelector('.analyze-scope')
  if (old) old.replaceWith(buildScopePicker())
}

function scopeToValue(s: AnalyzeScope): string {
  if (s.kind === 'named') return `named:${s.name}`
  if (s.kind === 'zone') return `zone:${s.id}`
  if (s.kind === 'country') return `country:${s.id}`
  return s.kind
}

function valueToScope(value: string): AnalyzeScope {
  if (value === 'view') return { kind: 'view' }
  if (value === 'box' && scope.kind === 'box') return scope
  if (value.startsWith('named:')) return { kind: 'named', name: value.slice(6) }
  if (value.startsWith('country:')) return { kind: 'country', id: value.slice(8) }
  if (value.startsWith('zone:') && value.slice(5) in ANALYZE_ZONES) return { kind: 'zone', id: value.slice(5) as AnalyzeZoneId }
  return { kind: 'dataset' }
}

/**
 * Resolve the picked scope to a geographic box.
 *
 * Three outcomes, deliberately distinct. `null` used to mean both
 * "analyse everything" and "I could not work out what you picked",
 * which turned an unresolvable region into whole-dataset statistics
 * wearing that region's name. A wrong answer delivered confidently is
 * worse than an error, so the unknown case is now its own state.
 */
type ScopeBounds =
  | { kind: 'all' }
  /** AYNI: `country` when the box is a country's, whose cells are its outline's (`regionWindow`). */
  | { kind: 'box'; bounds: LatLonBounds; country?: Country }
  | { kind: 'unknown' }
  /** AYNI: a country whose outlines have not arrived yet. */
  | { kind: 'loading' }

function boundsForScope(s: AnalyzeScope, src: AnalyzeSource): ScopeBounds {
  if (s.kind === 'view') {
    const bounds = src.visibleBounds()
    // No map yet is "everything", not an error: the whole dataset is a
    // truthful answer to "what can I see" before the camera exists.
    return bounds ? { kind: 'box', bounds } : { kind: 'all' }
  }
  if (s.kind === 'named') {
    const region = resolveRegion(s.name)
    if (!region) return { kind: 'unknown' }
    const [w, south, e, n] = region.bounds
    return { kind: 'box', bounds: { n, s: south, w, e } }
  }
  if (s.kind === 'zone') return { kind: 'box', bounds: { ...ANALYZE_ZONES[s.id] } }
  if (s.kind === 'box') return { kind: 'box', bounds: s.bounds }
  if (s.kind === 'country') {
    if (!loadedCountries()) return { kind: 'loading' }
    const country = countryById(s.id)
    return country ? { kind: 'box', bounds: { ...country.bounds }, country } : { kind: 'unknown' }
  }
  return { kind: 'all' }
}

/**
 * AYNI (owner, 2026-10-18): the cells a resolved region covers on a frame's
 * grid: a country's outline (its box with a mask), a box's rectangle, or
 * undefined for the whole dataset; null when the region misses the dataset.
 */
function regionWindow(scoped: ScopeBounds, snapshot: LumaSnapshot, options: DatasetOverlayOptions | undefined): TexelWindow | null | undefined {
  if (scoped.kind !== 'box') return undefined
  if (scoped.country) return countryCells(scoped.country, snapshot, options)?.window ?? null
  return windowForBounds(snapshot, scoped.bounds, options)
}

function refresh(body: HTMLElement): void {
  changeDrawn = false
  try {
    refreshBody(body)
  } finally {
    // AYNI: nothing on this frame to draw (no dataset, no baseline, the
    // map turned off): the globe must not keep the last one.
    if (!changeDrawn) takeChangeOff()
    // AYNI: the colour dialog's button is gone (Off, another tab): the dialog goes too.
    if (changeDialog && !changeDialog.anchor.isConnected) changeDialog.close()
  }
}

function refreshBody(body: HTMLElement): void {
  body.replaceChildren()
  lastResult = null
  lastWindow = undefined
  lastZonal = null
  lastFrame = null
  lastCompare = null
  transectHost = null
  hovHost = null
  contourHost = null
  // Anything already drawn was extracted against the *previous* frame
  // and the previous region window, so it cannot survive a recompute.
  // Changing the scope from the whole dataset to Alabama and leaving the
  // old lines up would put whole-dataset contours on the globe beside a
  // panel quoting Alabama's areas — the same annotation-outliving-its-
  // explanation failure the playback watch exists to prevent, one
  // trigger further along.
  clearContours()

  const src = source
  if (!src) {
    body.appendChild(message(t('analyze.empty.unavailable')))
    return
  }
  const frame = src.frame()
  lastFrame = frame
  if (!frame) {
    // No data-encoded dataset loaded, or no WebGL2. Absent rather than
    // broken, which is the availability posture the rest of the
    // data-encoded work takes. The box goes with the numbers it was
    // explaining.
    src.regionOutline?.()?.clear()
    clearContours()
    body.appendChild(message(t('analyze.empty.noDataset')))
    return
  }

  // AYNI: tabs (owner, 2026-10-06). Contours only where there is a globe to draw on.
  // AYNI (owner, 2026-10-17): no contours for Education.
  const panels = buildTabs(body, new Set<AnalyzeTab>(src.contours?.() && !teachingPlan() ? [] : ['contours']))
  renderRegionBlock(panels.stats, src, frame, panels.baseline)

  // Gated on the region block having produced numbers, unlike the
  // transect below. This profile describes the same window those
  // statistics cover, so under an "outside the dataset" message it would
  // be a chart of nothing captioned as a chart of something.
  if (lastResult) {
    const zonalHost = document.createElement('section')
    zonalHost.className = 'analyze-zonal-section'
    panels.profiles.appendChild(zonalHost)
    renderZonalSection(zonalHost, frame, lastWindow)
  }

  // Appended whatever the region block decided, and deliberately so: a
  // named region can be empty while the dataset is full, and the line a
  // viewer wants to draw is very often the one that leaves that box.
  // Only the globe's absence removes this — availability, not state.
  if (src.transect?.()) {
    transectHost = document.createElement('section')
    transectHost.className = 'analyze-transect-section'
    panels.profiles.appendChild(transectHost)
    // AYNI: the Line over time (research tier, slice 3), drawn with the line.
    hovHost = document.createElement('section')
    hovHost.className = 'analyze-hov-section'
    panels.profiles.appendChild(hovHost)
    renderTransectSection()
  }

  // Same availability-not-state rule as the transect: present whenever
  // there is a globe to draw on. Unlike the transect it needs the
  // region block to have produced a histogram, which `renderContourSection`
  // checks for itself rather than being gated here — the two reasons a
  // section can be absent should not be spelled in two places.
  if (src.contours?.()) {
    contourHost = document.createElement('section')
    contourHost.className = 'analyze-contour-section'
    panels.contours.appendChild(contourHost)
    renderContourSection()
  }

  // AYNI: Compare (research tier, 2026-10-06).
  renderCompareBlock(panels.compare, src, frame)
  // A tab with nothing to show says so rather than standing empty.
  for (const id of ANALYZE_TABS) {
    if (!panels[id].childElementCount) panels[id].appendChild(message(t('analyze.tab.empty')))
  }
  // AYNI: a figure of the view and the methods paragraph, under every tab (research tier, §Next S4/S5).
  renderFigureActions(body, src)
}

/**
 * AYNI: **Figure**, **Methods** and **Bundle (.zip)** (research tier, §Next
 * S4/S5/S8), below the tabs so they are there whatever was measured.
 * Research tools (decision R2): open on the plans that have them, a lock
 * that opens the plans on a plan with the data tools, nothing for a class
 * borrowing them.
 */
function renderFigureActions(body: HTMLElement, src: AnalyzeSource): void {
  if (!src.figure) return
  const row = document.createElement('div')
  row.className = 'analyze-figure-actions'
  const open = anomaliesAllowed()
  if (!open && (!researchUpsellShown())) return
  for (const [tab, key, cls] of [['figure', 'research.figure.open', 'analyze-figure-open'], ['methods', 'research.methods.open', 'analyze-methods-open']] as const) {
    const btn = actionButton(t(key), () => {
      if (open) src.figure?.(btn, tab)
      else openPlans(btn, 'research', { notice: t('plans.locked.figure') })
    })
    btn.classList.add(cls)
    if (!open) btn.classList.add('analyze-locked')
    row.appendChild(btn)
  }
  // AYNI (S8): everything above and the record in one zip, with a manifest of SHA-256.
  const status = document.createElement('p')
  status.className = 'analyze-coverage analyze-bundle-status'
  status.setAttribute('role', 'status')
  status.setAttribute('aria-live', 'polite')
  status.textContent = bundleStatus
  if (src.bundle) {
    const bundle = actionButton(t('research.bundle.open'), () => {
      if (!open) {
        openPlans(bundle, 'research', { notice: t('plans.locked.figure') })
        return
      }
      if (bundle.getAttribute('aria-busy') === 'true') return
      bundle.setAttribute('aria-busy', 'true')
      setBundleStatus(t('research.bundle.working'))
      void src.bundle!(bundle)
        .then((saved) => {
          setBundleStatus(saved
            ? t('research.bundle.saved', { file: saved.filename, count: formatNumber(saved.files), sha: saved.sha256 })
            : t('research.bundle.nothing'))
        })
        .catch(() => {
          setBundleStatus(t('research.bundle.failed'))
        })
        .finally(() => bundle.removeAttribute('aria-busy'))
    })
    bundle.classList.add('analyze-bundle-open')
    if (!open) bundle.classList.add('analyze-locked')
    row.appendChild(bundle)
  }
  body.appendChild(row)
  if (open && src.bundle) body.appendChild(status)
  if (!open) body.appendChild(caption(t('research.figure.locked')))
}

/** AYNI: the regions in English, for the methods paragraph (pasted into a paper, like the citation). */
const ZONE_ENGLISH: Record<AnalyzeZoneId, string> = {
  tropics: 'the tropics',
  northern: 'the Northern Hemisphere',
  southern: 'the Southern Hemisphere',
  arctic: 'the Arctic',
  antarctic: 'the Antarctic',
}

/** AYNI: a scope's value (`scopeToValue`) as an English phrase. */
function scopeEnglish(value: string): string {
  if (value === 'view') return 'the area in view'
  if (value === 'box') return 'a drawn box'
  if (value.startsWith('zone:') && value.slice(5) in ZONE_ENGLISH) return ZONE_ENGLISH[value.slice(5) as AnalyzeZoneId]
  if (value.startsWith('named:')) return value.slice(6)
  if (value.startsWith('country:')) {
    const c = countryById(value.slice(8))
    return c ? c.en : value.slice(8)
  }
  return 'the whole dataset'
}

/** What the open panel has computed, for the methods paragraph (`research/methods.ts`). */
export interface AnalyzeMethodsState {
  readonly statistics: MethodsStatistics | null
  readonly baseline: MethodsBaseline | null
  readonly change: MethodsChange | null
  readonly line: MethodsLine | null
  readonly compare: MethodsCompare | null
}

/**
 * AYNI: what the Analyze panel holds now, each part only when it was
 * computed (research tier, §Next S5). Null with the panel closed: its
 * numbers went with it.
 */
export function analyzeMethodsState(): AnalyzeMethodsState | null {
  if (!root || !source) return null
  let statistics: MethodsStatistics | null = null
  if (lastResult) {
    const scoped = boundsForScope(scope, source)
    const bounds: LatLonBox | null = scoped.kind === 'box' ? { ...scoped.bounds } : null
    const value = scopeToValue(scope)
    statistics = {
      region: { kind: scope.kind, label: scope.kind === 'box' && scope.label ? scope.label : scopeEnglish(value), bounds },
      count: lastResult.stats.count,
      coverage: lastResult.stats.coverage,
      zonal: lastZonal !== null,
    }
  }
  const held = anomaliesAllowed() ? baseline : null
  const period = anomaliesAllowed() && periodState?.clim && periodState.field ? periodState : null
  let baselineState: MethodsBaseline | null = null
  if (held) {
    baselineState = {
      kind: 'frame',
      frameLabel: held.frameLabel,
      otherDataset: held.datasetId !== source.datasetId() ? held.datasetTitle : null,
      // The panel's label is in the interface language; the key starts with the scope's value.
      regionLabel: scopeEnglish(held.regionKey.split('|')[0]),
      compared: lastFit === 'applies' && statistics !== null,
    }
  } else if (period) {
    // S2: the period as it was computed (only once the climatology is finished and held).
    const clim = period.clim!
    const cal = period.calendar
    const run = period.run
    const axis = run.spec.axis
    const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10)
    const now = groupNow(cal)
    baselineState = {
      kind: 'period',
      startLabel: period.period ? String(period.period.from) : iso(axis.startMs),
      endLabel: period.period ? String(period.period.to) : iso(climateFrameTimeMs(axis, axis.frameCount - 1)),
      framesRead: run.read,
      framesInPeriod: run.total,
      otherDataset: period.datasetId !== source.datasetId() ? period.datasetTitle : null,
      regionLabel: null,
      compared: lastFit === 'applies' && periodAnomalyNow !== null && statistics !== null,
      climatology: {
        grouping: cal.grouping,
        monthsPerFrame: cal.monthsPerFrame,
        slotHours: cal.grouping === 'subdaily' ? cal.slotHours : null,
        windowReason: cal.windowReason,
        skipped: run.check.skipped,
        early: run.read < run.total,
        doubleMean: Math.max(0, ...run.check.framesPerGroup) > 255,
        factor: clim.factor,
        minYears: Math.min(...expectedGroups(cal).map((g) => clim.yearsPerGroup[g] ?? 0)),
        sigma: clim.hasSigma,
        sigmaCells: clim.cellsWithSigma,
        flooredCells: clim.flooredCells,
        groupNow: now !== null ? groupLabel(cal, now) : null,
      },
    }
  }
  const view = changeViewState()
  let change: MethodsChange | null = null
  if (held?.field && view.field && !view.field.period && !view.other && view.mode && view.mode !== 'sigma' && changeShown(view)) {
    const step = quantStep(held.field.scale as DisplayColorScale)
    const rule = view.mode === 'percent' && view.floor !== null ? floorRule(view.floor, step) : null
    change = {
      mode: view.mode,
      limit: view.limit,
      range: validRange(view.range) ? view.range : null,
      colours: defaultChangeColours(view.colours) ? null : view.colours,
      floor: rule && view.floor !== null
        ? (rule.rule === 'steps' ? { value: view.floor, rule: 'steps', step: rule.step } : { value: view.floor, rule: 'typical', typical: rule.typical })
        : null,
      drawn: liveKey !== null ? 'live' : 'paused',
    }
  } else if (period && view.field === period.field && !view.other && view.mode && changeShown(view)) {
    const step = quantStep(period.scale)
    const rule = view.mode === 'percent' && view.floor !== null ? floorRule(view.floor, step) : null
    change = {
      mode: view.mode,
      limit: view.limit,
      range: validRange(view.range) ? view.range : null,
      colours: defaultChangeColours(view.colours) ? null : view.colours,
      floor: rule && view.floor !== null
        ? (rule.rule === 'steps' ? { value: view.floor, rule: 'steps', step: rule.step } : { value: view.floor, rule: 'typical', typical: rule.typical })
        : null,
      drawn: liveKey !== null ? 'live' : 'paused',
    }
  }
  let line: MethodsLine | null = null
  if (transectEnds && lastTransect && lastTransect.samples.length > 1) {
    const samples = lastTransect.samples
    line = { from: { ...transectEnds.from }, to: { ...transectEnds.to }, lengthKm: samples[samples.length - 1].distanceKm, points: samples.length, ...(transectEnds.path === 'parallel' ? { path: 'parallel' as const } : {}) }
  }
  const compare = lastCompare && (compareMode !== null || compareSeen || activeTab === 'compare') ? { ...lastCompare, mapped: compareMode !== null } : null
  return { statistics, baseline: baselineState, change, line, compare }
}

/**
 * AYNI: Compare — the dataset (A) minus a real-time overlay that carries
 * values (B), on a common grid, with the statistics model evaluation uses
 * (`research/compare.ts`). Refuses rather than compares when the units have
 * no known conversion, the frames are apart in time, or either is made of
 * classes; warns when the two look like different quantities.
 */
function renderCompareBlock(
  host: HTMLElement,
  src: AnalyzeSource,
  frame: { snapshot: LumaSnapshot; scale: DisplayColorScale; options: DatasetOverlayOptions },
): void {
  // AYNI (S8): set again below when a comparison is on screen.
  compareCsv = null
  // AYNI (the chatbot's compare.run): what this render found, set at each way out.
  compareChat = { ok: false, reason: 'no-values' }
  const off = (): void => {
    if (compareMode && changeViewState().other) takeChangeOff()
    compareMode = null
  }
  if (!researchToolsAllowed()) {
    off()
    if (!researchUpsellShown()) return
    const locked = actionButton(t('research.compare.title'), () => openPlans(locked, 'research', { notice: t('plans.locked.compare') }))
    locked.classList.add('analyze-locked')
    host.append(locked, caption(t('research.compare.locked')))
    return
  }
  const all = src.compareCandidates?.() ?? []
  const overlays = all.filter((c) => (c.kind ?? 'overlay') === 'overlay')
  const globes = all.filter((c) => c.kind === 'globe')
  // AYNI: B is a real-time overlay or another globe (slice 2); the first that has one, until the viewer picks.
  const source: CompareSource = compareSource ?? (overlays.length || !globes.length ? 'overlay' : 'globe')
  host.appendChild(compareSegmented<CompareSource>(
    'analyze-compare-source', t('research.compare.source'), t('research.compare.sourceAria'),
    [['overlay', t('research.compare.sourceOverlay')], ['globe', t('research.compare.sourceGlobe')]],
    source,
    (value) => {
      if (value === source) return
      off()
      compareSource = value
    },
  ))
  const lead = document.createElement('p')
  lead.className = 'analyze-compare-lead'
  lead.textContent = t(source === 'globe' ? 'research.compare.leadGlobe' : 'research.compare.lead')
  host.appendChild(lead)
  const candidates = source === 'globe' ? globes : overlays
  if (!candidates.length) {
    off()
    compareChat = { ok: false, reason: 'no-candidates' }
    host.appendChild(message(t(source === 'globe' ? 'research.compare.noGlobes' : 'research.compare.none')))
    return
  }
  if (!candidates.some((c) => c.id === compareId)) {
    off()
    compareId = candidates[0].id
  }
  const b = candidates.find((c) => c.id === compareId)!
  const nameOf = (c: CompareCandidate): string => (c.kind === 'globe' ? t('research.compare.globeName', { n: String(c.globe ?? ''), name: c.title }) : c.title)
  if (candidates.length > 1) {
    const select = document.createElement('select')
    select.className = 'analyze-scope-select analyze-compare-pick'
    select.setAttribute('aria-label', t(source === 'globe' ? 'research.compare.pickGlobe' : 'research.compare.pick'))
    for (const c of candidates) {
      const opt = document.createElement('option')
      opt.value = c.id
      opt.textContent = nameOf(c)
      select.appendChild(opt)
    }
    select.value = b.id
    select.addEventListener('change', () => {
      off()
      chatOwned.compare = false
      compareId = select.value
      rerenderPanel('.analyze-compare-pick')
    })
    host.appendChild(select)
  }
  const titleA = src.datasetTitle() ?? ''
  const nameA = source === 'globe' ? t('research.compare.primaryName', { name: titleA }) : titleA
  for (const [key, name] of [['research.compare.a', nameA], ['research.compare.b', nameOf(b)]] as const) {
    const what = document.createElement('p')
    what.className = 'analyze-compare-what'
    what.textContent = t(key, { name })
    host.appendChild(what)
  }
  if (source === 'globe') host.appendChild(caption(t('research.compare.primaryNote')))

  // Refusals: classes, units, dates, data.
  const kindA = (frame.scale as { release?: { kind?: string } }).release?.kind
  if (b.classified || kindA === 'luma8-classified') {
    off()
    compareChat = { ok: false, reason: 'classified' }
    host.appendChild(message(t('research.compare.classified')))
    return
  }
  const change = isChangeTitle(titleA) || isChangeTitle(b.title)
  const convert = unitConversion(b.units, frame.scale.units, change)
  if (!convert) {
    off()
    compareChat = { ok: false, reason: 'units' }
    const refusal = message(t('research.compare.units', { a: frame.scale.units ?? '—', b: b.units ?? '—' }))
    refusal.classList.add('analyze-compare-refused')
    host.appendChild(refusal)
    return
  }
  if (convert.factor !== 1 || convert.offset !== 0) {
    host.appendChild(caption(t(convert.offset !== 0 ? 'research.compare.convertedOffset' : 'research.compare.converted', {
      from: b.units ?? '',
      to: frame.scale.units ?? '',
      factor: formatNumber(convert.factor, { maximumSignificantDigits: 6 }),
      offset: formatNumber(convert.offset, { maximumSignificantDigits: 6 }),
    })))
  }
  const warning = quantityWarning(titleA, b.title)
  if (warning) {
    const hint = document.createElement('p')
    hint.className = 'analyze-change-hint'
    hint.textContent = t(warning === 'change' ? 'research.compare.warnChange' : 'research.compare.warnSubject')
    host.appendChild(hint)
  }

  // Which frames: the same date (within half the coarser step), or as each globe shows them.
  host.appendChild(compareSegmented<FramePairing>(
    'analyze-compare-pairing', t('research.compare.frames'), t('research.compare.framesAria'),
    [['time', t('research.compare.byDate')], ['shown', t('research.compare.asShown')]],
    comparePairing,
    (value) => { comparePairing = value },
  ))
  const ia = src.frameInstant?.() ?? null
  const ib = b.instant()
  const pair = pairFor(comparePairing, ia, ib)
  const date = (i: FrameInstant | null): string =>
    (i ? formatFrameTime(i.ms, precisionOfCadence(i.cadenceMs)) : '—')
  if (pair.status === 'apart' || !b.visible()) {
    off()
    compareChat = { ok: false, reason: pair.status === 'apart' ? 'apart' : 'outside-dates' }
    host.appendChild(message(t('research.compare.apart', { a: date(ia), b: date(ib) })))
    // A globe can be moved to A's date (its stream may not follow the globes' date sync).
    if (b.seekTo && ia && pair.status === 'apart') {
      const move = actionButton(t('research.compare.moveB', { n: String(b.globe ?? ''), date: date(ia) }), () => {
        move.disabled = true
        void b.seekTo!(ia.ms).then(() => setTimeout(() => rerenderPanel('.analyze-compare-pairing[data-value="time"]'), 300))
      })
      move.classList.add('analyze-compare-move')
      host.appendChild(move)
    }
    return
  }
  host.appendChild(caption(pair.status === 'paired'
    ? t('research.compare.paired', { a: date(ia), b: date(ib) })
    : pair.status === 'shown'
      ? t('research.compare.shown', { a: date(ia), b: date(ib) })
      : t('research.compare.datesUnknown')))
  const fieldB = b.frame()
  if (!fieldB) {
    off()
    compareChat = { ok: false, reason: 'waiting' }
    host.appendChild(message(t('research.compare.waiting')))
    return
  }

  // % of B only where both are ratio quantities (zero means none).
  const ratioA = ratioRefusal(frame.scale, titleA)
  const ratioB = ratioRefusal(fieldB.scale, b.title)
  const ratio = ratioA === null && ratioB === null
  if (compareMode === 'percent' && !ratio) {
    compareMode = 'difference'
    if (compareAskedPercent) compareChat = { ok: false, reason: `ratio:${ratioA ?? ratioB}` }
  }
  const percentRefused = compareAskedPercent && !ratio
  compareAskedPercent = false
  const mode = compareMode ?? 'difference'
  const scoped = boundsForScope(scope, src)
  const region = scoped.kind === 'box' ? scoped.bounds : null
  const key = `${b.id}|${convert.factor}|${convert.offset}|${mode}`
  // For the methods paragraph: the % of B's floor rule, when one is drawn.
  let compareFloor: MethodsCompare['floor'] = null
  const result = compareFields(frame, fieldB, convert, {
    mode,
    region,
    limit: compareLimit?.key === key ? compareLimit.limit : null,
    range: compareRange?.key === key ? compareRange.range : null,
    floor: compareLimit?.key === key ? compareLimit.floor : null,
  })
  if (compareLimit?.key !== key) compareLimit = { key, limit: result.grid.limit, floor: result.grid.floor }
  const units = frame.scale.units
  const legendTitle = mode === 'percent' ? t('research.compare.legendPercent') : t('research.compare.legend', { units: units ?? '' })

  // On the globe: Off · A − B · % of B.
  const modes: Array<[CompareMode | null, string]> = [[null, t('research.change.off')], ['difference', t('research.compare.aMinusB')]]
  if (ratio) modes.push(['percent', t('research.compare.percentOfB')])
  const group = compareSegmented<CompareMode | null>(
    'analyze-compare-mode', t('research.change.label'), t('research.compare.globeAria'),
    modes,
    compareMode,
    (value) => {
      if (value === null) off()
      else compareMode = value
    },
  )
  host.appendChild(group)
  if (!ratio) {
    const why = ratioA ?? ratioB
    host.appendChild(caption(t('research.compare.noPercent', { why: t(`research.compare.ratio.${why}` as MessageKey) })))
  }

  if (compareMode && typeof document !== 'undefined') {
    host.appendChild(compareSegmented<ChangeUpdate>(
      'analyze-compare-update', t('research.change.update'), t('research.change.updateAria'),
      [['live', t('research.change.live')], ['pause', t('research.change.pause')]],
      changeViewState().update,
      (update) => {
        // Asking for While playing again tries the graphics card again.
        if (update === 'live') liveRefused = false
        setChangeView({ update })
      },
    ))
    const colours = changeViewState().colours
    const tables = buildLiveCompare(frame, fieldB, convert, result.grid, colours)
    // One map on the globe at a time: the comparison replaces the baseline's change.
    let live = false
    if (changeViewState().update === 'live' && src.liveChange && !liveRefused && tables) {
      const feed = b.live?.() ?? null
      const liveId = [
        'compare', b.id, mode, result.grid.range.min, result.grid.range.max, colours.palette, colours.inverted, result.grid.floor, result.grid.width, tables.ka, tables.kb,
        convert.factor, convert.offset, frame.scale.vmin, frame.scale.vmax, fieldB.scale.vmin, fieldB.scale.vmax,
        frame.snapshot.width, frame.snapshot.height, fieldB.snapshot.width, fieldB.snapshot.height,
      ].join('|')
      if (feed && liveCurrent(src, liveId) && feed.source === liveCompareSource) {
        live = true
      } else if (feed) {
        if (liveKey !== null) src.liveChange(null)
        liveKey = null
        live = src.liveChange({ compare: { ...tables, source: feed.source, crop: feed.crop } })
        if (live) {
          liveSent(src, liveId)
          liveCompareSource = feed.source
        } else liveRefused = true
      }
    }
    if (live) {
      src.showChange?.(null)
    } else {
      if (liveKey !== null) src.liveChange?.(null)
      liveKey = null
      const canvas = document.createElement('canvas')
      canvas.width = result.grid.width
      canvas.height = result.grid.height
      // Painted by the shader's own arithmetic, so the paused and the live picture agree.
      const pixels = tables ? paintLiveCompare(tables, frame.snapshot, fieldB.snapshot) : paintChangeGrid(result.grid, 235, undefined, colours)
      canvas.getContext('2d')?.putImageData(new ImageData(pixels, result.grid.width, result.grid.height), 0, 0)
      src.showChange?.({ canvas, frameKey: src.frameKey?.() ?? null })
    }
    src.compareActive?.(b.id)
    const conv = (v: number | null): number | null => (v === null ? null : convert.factor * v + convert.offset)
    setChangeView({
      mode,
      limit: result.grid.limit,
      // AYNI: Compare's own range, sent with `other` (a new one each redraw, which would drop it).
      range: compareRange?.key === key ? compareRange.range : null,
      floor: result.grid.floor,
      other: {
        legendTitle,
        label: nameOf(b),
        title: b.title,
        // The same pixels as the paused picture, for the figure while the graphics card draws it live.
        picture: () => {
          if (typeof document === 'undefined') return null
          const c = document.createElement('canvas')
          c.width = result.grid.width
          c.height = result.grid.height
          const pixels = tables ? paintLiveCompare(tables, frame.snapshot, fieldB.snapshot) : paintChangeGrid(result.grid, 235, undefined, colours)
          c.getContext('2d')?.putImageData(new ImageData(pixels, result.grid.width, result.grid.height), 0, 0)
          return c
        },
        valueAt: (lat, lon) => conv(b.valueAt(lat, lon)),
        seriesAt: (pinId) => {
          const raw = src.compareSeriesAt?.(pinId) ?? null
          return raw ? { ...raw, slots: raw.slots.map((slot: MeasurementSeries['slots'][number]) => (slot.sampled ? { sampled: true, value: conv(slot.value) } : slot)) } : null
        },
      },
    })
    setChangeView({ shown: true })
    changeDrawn = true
    const compareTarget: RangeTarget = {
      shown: () => (compareRange?.key === key ? compareRange.range : compareLimit?.key === key ? symmetricRange(compareLimit.limit) : null),
      own: () => compareRange?.key === key,
      set: (range) => {
        compareRange = range ? { key, range } : null
      },
    }
    const legend = buildChangeLegend({ mode, limit: result.grid.range.max, min: result.grid.range.min, units, title: legendTitle, colours })
    host.appendChild(withColoursButton(legend, changeColoursButton(result.grid, units, compareTarget)))
    host.appendChild(renderChangeRange(result.grid, units, rerenderPanel, compareTarget))
    const greyKey = document.createElement('p')
    greyKey.className = 'analyze-change-grey'
    const swatch = document.createElement('span')
    swatch.className = 'analyze-change-swatch'
    swatch.style.background = `rgb(${CHANGE_GREY.join(', ')})`
    swatch.setAttribute('aria-hidden', 'true')
    greyKey.append(swatch, document.createTextNode(t(mode === 'percent' ? 'research.compare.greyPercent' : 'research.compare.grey')))
    host.appendChild(greyKey)
    // The exact threshold, and which rule set it.
    if (mode === 'percent' && result.grid.floor !== null) {
      const stepB = linearStep(fieldB.scale)
      const rule = floorRule(result.grid.floor, stepB !== null ? Math.abs(convert.factor) * stepB : null)
      compareFloor = rule.rule === 'steps' ? { value: result.grid.floor, rule: 'steps', step: rule.step } : { value: result.grid.floor, rule: 'typical', typical: rule.typical }
      const total = result.grid.painted + result.grid.masked
      host.appendChild(caption(t('research.compare.percentRule', {
        floor: formatStatValue(result.grid.floor, units),
        count: formatNumber(result.grid.masked),
        share: formatNumber(total > 0 ? (100 * result.grid.masked) / total : 0, { maximumFractionDigits: total > 0 && result.grid.masked / total < 0.01 ? 1 : 0 }),
        rule: rule.rule === 'steps'
          ? t('research.compare.floorSteps', { step: formatStatValue(rule.step, units) })
          : t('research.compare.floorTypical', { typical: formatStatValue(rule.typical, units) }),
      })))
    }
    const update = changeViewState().update
    host.appendChild(caption(t(live ? 'research.compare.liveNote' : update === 'live' ? 'research.compare.liveRefused' : 'research.compare.whenPaused')))
    if (source === 'overlay') host.appendChild(caption(t('research.compare.overlayHidden')))
  }

  // One region on each side, side by side (the Statistics tab's region).
  const regionName = scopeLabel(scope)
  const sides = regionSides(scoped, frame, fieldB, convert, ratio)
  if (sides) {
    host.appendChild(renderRegionSides(sides, nameA, nameOf(b), regionName, units, mode === 'percent'))
  } else if (scoped.kind === 'unknown') {
    host.appendChild(message(t('analyze.empty.unknownRegion')))
  }

  if (!result.stats) {
    compareChat = { ok: false, reason: 'no-overlap' }
    host.appendChild(message(t('research.compare.noOverlap')))
    return
  }
  const st = result.stats
  compareChat = percentRefused
    ? { ok: false, reason: `ratio:${ratioA ?? ratioB}` }
    : {
        ok: true,
        result: {
          source,
          other: nameOf(b),
          stats: st,
          ratio: ratio && Number.isFinite(st.relativeBias),
          cellDegrees: result.cellDegrees,
          resolution: result.resolution,
          pairing: { status: pair.status, gapMs: pair.gapMs },
          frames: ia && ib ? { a: date(ia), b: date(ib) } : null,
          units: units ?? null,
          warning,
          region: scope.kind === 'box' && scope.label ? scope.label : scopeEnglish(scopeToValue(scope)),
          mapped: compareMode,
        },
      }
  // AYNI: for the methods paragraph (§Next S5): what was compared, and how.
  if (activeTab === 'compare') compareSeen = true
  lastCompare = {
    otherTitle: b.title,
    otherUnits: b.units ?? null,
    conversion: convert.factor !== 1 || convert.offset !== 0 ? { factor: convert.factor, offset: convert.offset } : null,
    cellDegrees: result.cellDegrees,
    frames: (pair.status === 'paired' || pair.status === 'shown') && ia && ib ? { a: date(ia), b: date(ib) } : null,
    mapped: compareMode !== null,
    source,
    pairing: comparePairing,
    mode: compareMode,
    range: compareMode !== null && compareRange?.key === key ? compareRange.range : null,
    colours: compareMode !== null && !defaultChangeColours(changeViewState().colours) ? changeViewState().colours : null,
    floor: compareFloor,
    ratio: ratio && Number.isFinite(st.relativeBias),
    sides: !!sides,
  }
  // AYNI: a research record, when one is recording, keeps the comparison's numbers (slice 4).
  trail({
    type: 'compare',
    ...actorOf(chatOwned.compare),
    frame: true,
    coalesce: `compare|${b.title}`,
    data: {
      datasetA: src.datasetTitle(), datasetB: b.title, units: units ?? '', bInUnitsOfA: `${convert.factor} * b + ${convert.offset}`,
      source, ...(source === 'globe' && b.globe ? { globeB: b.globe } : {}),
      frameA: date(ia), frameB: date(ib), pairing: comparePairing === 'shown' ? 'as shown (dates stated, not matched)' : `by date (${pair.status})`, gridDegrees: result.cellDegrees,
      onGlobe: compareMode === 'percent' ? '% of B' : compareMode === 'difference' ? 'A - B' : 'off',
      ...(compareFloor ? { percentFloor: compareFloor.value, percentFloorRule: compareFloor.rule } : {}),
      ...(ratio && Number.isFinite(st.relativeBias) ? { biasPercentOfB: st.relativeBias } : {}),
      ...(sides ? { regionSides: sides.rows.map((row) => ({ statistic: row.key, a: row.a, b: row.b, difference: row.difference, ...(row.percent !== null ? { percentOfB: row.percent } : {}) })) } : {}),
      bias: st.bias, rmsd: st.rmsd, centredRmsd: st.centredRmsd, patternCorrelation: st.patternCorrelation, meanA: st.meanA, meanB: st.meanB, cells: st.cells, areaKm2: st.areaKm2, overlap: st.overlap,
      method: 'A minus B on a common grid at the coarser resolution, area-weighted; bias, RMS difference, centred RMS difference and pattern correlation',
      summary: `${src.datasetTitle() ?? 'A'} minus ${b.title}: bias ${recNum(st.bias)} ${units ?? ''}, RMS ${recNum(st.rmsd)}, r ${recNum(st.patternCorrelation)}`,
    },
  })
  const tiles = document.createElement('div')
  tiles.className = 'analyze-stats analyze-compare-tiles'
  const signed = (v: number): string => (v > 0 ? '+' : v < 0 ? '−' : '') + formatStatValue(Math.abs(v), units)
  const list: Array<[string, string]> = [
    [t('research.compare.bias'), signed(st.bias)],
    [t('research.compare.rmsd'), formatStatValue(st.rmsd, units)],
    [t('research.compare.centred'), formatStatValue(st.centredRmsd, units)],
    [t('research.compare.r'), Number.isFinite(st.patternCorrelation) ? formatNumber(st.patternCorrelation, { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : '—'],
    [t('research.compare.meanA'), formatStatValue(st.meanA, units)],
    [t('research.compare.meanB'), formatStatValue(st.meanB, units)],
  ]
  if (ratio && Number.isFinite(st.relativeBias)) {
    list.push([t('research.compare.relativeBias'), `${st.relativeBias > 0 ? '+' : st.relativeBias < 0 ? '−' : ''}${formatNumber(Math.abs(st.relativeBias), { maximumFractionDigits: 1 })} %`])
  }
  for (const [l, v] of list) tiles.appendChild(renderStatTile(l, v))
  host.appendChild(tiles)
  host.appendChild(caption(t('research.compare.over', {
    area: formatStatValue(st.areaKm2, t('analyze.units.km2')),
    share: formatNumber(st.overlap * 100, { maximumFractionDigits: 0 }),
    deg: formatNumber(result.cellDegrees, { maximumFractionDigits: 2 }),
  })))
  if (region) host.appendChild(caption(t('research.compare.overRegion', { region: regionName })))
  host.appendChild(caption(result.resolution !== null
    ? t('research.compare.resolution', { step: formatStatValue(result.resolution, units) })
    : t('research.compare.resolutionUneven')))
  host.appendChild(caption(t('research.compare.method')))
  host.appendChild(caption(t('research.compare.limits')))
  compareCsv = () => ({
    name: ayniCsvFilename('compare', src.datasetTitle(), new Date(), b.title),
    text: buildCompareCsvText({
          datasetA: src.datasetTitle(),
          datasetB: b.title,
          units,
          unitsB: b.units,
          conversion: convert,
          frameA: date(ia),
          frameB: date(ib),
          pairing: pair.status,
          cellDegrees: result.cellDegrees,
          resolution: result.resolution,
          stats: st,
          provenance: src.provenance?.() ?? undefined,
          // AYNI: two globes, % of B, the region side by side, and B's own provenance.
          source: b.kind === 'globe' ? `globe ${b.globe ?? ''}` : 'real-time overlay',
          mode,
          percentFloor: mode === 'percent' ? result.grid.floor : null,
          region: region ? regionName : null,
          regionRows: sides?.rows ?? null,
          provenanceB: b.provenance?.() ?? undefined,
        }),
  })
  if (!analysisExportsLocked()) {
    const csv = actionButton(t('analyze.export'), () => {
      const part = compareCsv?.()
      if (part) downloadCsv(part.name, part.text)
    })
    csv.classList.add('analyze-export', 'analyze-compare-csv')
    host.appendChild(csv)
  }
}

/** AYNI: Compare's own segmented control (source, frames, map, update); a pick re-renders the panel. */
function compareSegmented<T extends string | null>(cls: string, labelText: string, aria: string, options: Array<[T, string]>, current: T, pick: (v: T) => void): HTMLElement {
  const group = document.createElement('div')
  group.className = 'analyze-change-modes'
  group.setAttribute('role', 'group')
  group.setAttribute('aria-label', aria)
  const label = document.createElement('span')
  label.className = 'analyze-change-label'
  label.textContent = labelText
  group.appendChild(label)
  for (const [value, text] of options) {
    const btn = actionButton(text, () => {
      chatOwned.compare = false
      pick(value)
      rerenderPanel(`.${cls}[data-value="${value ?? 'off'}"]`)
    })
    btn.classList.add('analyze-change-mode', cls)
    btn.dataset.value = value ?? 'off'
    btn.setAttribute('aria-pressed', String(current === value))
    group.appendChild(btn)
  }
  return group
}

/** AYNI: the region's name as the Statistics tab's picker says it. */
function scopeLabel(s: AnalyzeScope): string {
  if (s.kind === 'dataset') return t('analyze.scope.dataset')
  if (s.kind === 'view') return t('analyze.scope.view')
  if (s.kind === 'zone') return t(ZONE_LABEL[s.id])
  if (s.kind === 'named') return s.name
  if (s.kind === 'country') {
    const c = countryById(s.id)
    return c ? countryName(c, getLocale()) : s.id
  }
  return s.label ?? t('analyze.scope.box')
}

/** AYNI: the region's statistics on each side (each over its own texels), or null when either has none there. */
function regionSides(
  scoped: ScopeBounds,
  frame: { snapshot: LumaSnapshot; scale: DisplayColorScale; options: DatasetOverlayOptions },
  fieldB: CompareField,
  convert: { factor: number; offset: number },
  ratio: boolean,
): { rows: RegionDifference[]; a: RegionStats; b: RegionStats } | null {
  if (scoped.kind === 'unknown') return null
  const winA = regionWindow(scoped, frame.snapshot, frame.options)
  const winB = scoped.kind === 'box' ? windowForBounds(fieldB.snapshot, scoped.bounds, fieldB.options) : undefined
  if (winA === null || winB === null) return null
  const a = summarize(frame.snapshot, frame.scale, frame.options, winA ?? undefined)
  const b = summarize(fieldB.snapshot, fieldB.scale, fieldB.options, winB ?? undefined)
  if (!a || !b) return null
  return { rows: regionDifferences(a, b, convert, ratio), a, b }
}

/** AYNI: the side-by-side table: one row per statistic, A, B (in A's units), A − B and, as a %, of B. */
function renderRegionSides(
  sides: { rows: RegionDifference[]; a: RegionStats; b: RegionStats },
  nameA: string,
  nameB: string,
  region: string,
  units: string | undefined,
  percent: boolean,
): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'analyze-compare-sides'
  const head = document.createElement('h3')
  head.textContent = t('research.compare.sidesTitle', { region })
  wrap.appendChild(head)
  const table = document.createElement('table')
  table.className = 'analyze-compare-table'
  const tr = (cells: string[], th = false): HTMLTableRowElement => {
    const row = document.createElement('tr')
    cells.forEach((text, i) => {
      const cell = document.createElement(th || i === 0 ? 'th' : 'td')
      if (th) cell.setAttribute('scope', 'col')
      else if (i === 0) cell.setAttribute('scope', 'row')
      cell.textContent = text
      row.appendChild(cell)
    })
    return row
  }
  const cols = [t('research.compare.statistic'), 'A', 'B', t('research.compare.aMinusB')]
  if (percent) cols.push(t('research.compare.percentOfB'))
  const thead = document.createElement('thead')
  thead.appendChild(tr(cols, true))
  table.appendChild(thead)
  const tbody = document.createElement('tbody')
  const labels: Record<RegionDifference['key'], MessageKey> = {
    mean: 'research.compare.stat.mean',
    median: 'research.compare.stat.median',
    p10: 'research.compare.stat.p10',
    p90: 'research.compare.stat.p90',
  }
  const signed = (v: number): string => (v > 0 ? '+' : v < 0 ? '−' : '') + formatStatValue(Math.abs(v), units)
  for (const r of sides.rows) {
    const cells = [t(labels[r.key]), formatStatValue(r.a, units), formatStatValue(r.b, units), signed(r.difference)]
    if (percent) cells.push(r.percent === null ? '—' : `${r.percent > 0 ? '+' : r.percent < 0 ? '−' : ''}${formatNumber(Math.abs(r.percent), { maximumFractionDigits: 1 })} %`)
    tbody.appendChild(tr(cells))
  }
  table.appendChild(tbody)
  wrap.appendChild(table)
  wrap.appendChild(caption(t('research.compare.sidesNote', {
    a: nameA,
    b: nameB,
    areaA: formatStatValue(sides.a.areaKm2, t('analyze.units.km2')),
    areaB: formatStatValue(sides.b.areaKm2, t('analyze.units.km2')),
  })))
  return wrap
}

/** AYNI: recompute the open panel, leaving the focus where it is (the colour dialog, outside it). */
function redrawPanel(): void {
  const host = root?.querySelector('.analyze-body') as HTMLElement | null
  if (host) refresh(host)
}

/** AYNI: recompute the open panel and put the focus back on `focusSelector`. */
function rerenderPanel(focusSelector: string): void {
  const host = root?.querySelector('.analyze-body') as HTMLElement | null
  if (!host) return
  refresh(host)
  ;(host.querySelector(focusSelector) as HTMLElement | null)?.focus()
}

function renderRegionBlock(
  body: HTMLElement,
  src: AnalyzeSource,
  frame: { snapshot: LumaSnapshot; scale: DisplayColorScale; options: DatasetOverlayOptions },
  /** AYNI: the Baseline tab's panel. */
  baselineHost: HTMLElement = body,
): void {
  const { snapshot, scale, options } = frame
  // AYNI: without full analysis only a drawn box is measured (owner,
  // 2026-10-05); the panel says so instead of quoting the whole region.
  if (scope.kind !== 'box' && regionStatsLocked()) {
    src.regionOutline?.()?.clear()
    body.appendChild(message(t('analyze.regionLocked')))
    return
  }
  const scoped = boundsForScope(scope, src)
  syncRegionOutline(src, scoped)
  if (scoped.kind === 'loading') {
    body.appendChild(message(t('analyze.country.loading')))
    void loadCountries().then(() => {
      const host = root?.querySelector('.analyze-body') as HTMLElement | null
      if (host) refresh(host)
    })
    return
  }
  if (scoped.kind === 'unknown') {
    body.appendChild(message(t('analyze.empty.unknownRegion')))
    return
  }
  const window = regionWindow(scoped, snapshot, options)
  if (scoped.kind === 'box' && !window) {
    body.appendChild(message(t('analyze.empty.outsideDataset')))
    return
  }

  const stats = summarize(snapshot, scale, options, window ?? undefined)
  if (!stats) {
    body.appendChild(message(t('analyze.empty.noValues')))
    return
  }
  const hist = buildHistogram(snapshot, scale, options, window ?? undefined)
  lastResult = { stats, hist, scale }
  lastWindow = window ?? undefined
  // AYNI: does a held baseline describe these texels, in these units?
  lastRegionKey = regionKey(scopeToValue(scope), window ?? undefined)
  // AYNI: a period's climatology covers every region of its grid: the frame
  // against it place by place over this one (not statistic by statistic:
  // a frame's extremes against a field of means would mean little).
  periodAnomalyNow = null
  if (periodState?.clim && anomaliesAllowed()) {
    lastFit = periodFit(stats.units, frame)
    if (lastFit === 'applies') periodAnomalyNow = periodAnomaly(frame, window ?? undefined)
  } else {
    lastFit = baseline && anomaliesAllowed() ? baselineFit(baseline, lastRegionKey, stats.units) : null
  }
  const deltas = lastFit === 'applies' && baseline && !periodState?.clim ? statDeltas(stats, baseline.stats) : null

  body.appendChild(renderHistogram(hist, scale, src.display()))
  body.appendChild(renderHistogramCaption(scale))
  body.appendChild(renderStats(stats, deltas, quantStep(scale)))
  if (periodAnomalyNow && periodState) body.appendChild(renderPeriodAnomaly(periodAnomalyNow, periodState, stats.units, quantStep(scale)))
  body.appendChild(renderCoverage(stats))
  const measuredAt = src.frameTime?.()
  // Which instant these numbers describe. These are animations, so a
  // statistic with no frame named is a claim about an unnamed moment —
  // the same reason Orbit's tool results carry a `frameTime`. Absent for
  // a dataset with no time label, where there is nothing to name.
  if (measuredAt) {
    body.appendChild(caption(t('analyze.frameTime', { time: measuredAt })))
  }
  body.appendChild(renderPrecisionNote(scale, options))
  // AYNI (owner, 2026-10-18): what a country is here: its outline's cells, or one cell when it is smaller than that.
  if (scoped.kind === 'box' && scoped.country) {
    const cells = countryCells(scoped.country, snapshot, options)
    body.appendChild(caption(cells?.tiny ? t('analyze.country.tiny') : t('analyze.country.outline')))
  }
  // AYNI: a research record, when one is recording, keeps these numbers (slice 4).
  if (trailOn()) {
    const label = root?.querySelector<HTMLSelectElement>('.analyze-scope-select')?.selectedOptions[0]?.textContent ?? scopeToValue(scope)
    const u = stats.units ?? ''
    trail({
      type: 'stats',
      ...actorOf(chatOwned.scope),
      frame: true,
      coalesce: `stats|${lastRegionKey}`,
      data: {
        region: label,
        bounds: scoped.kind === 'box' ? scoped.bounds : null,
        units: u,
        mean: stats.mean, median: stats.median, min: stats.min, max: stats.max, p10: stats.p10, p90: stats.p90, stdDev: stats.stdDev,
        areaKm2: stats.areaKm2, coverage: stats.coverage, texels: stats.count,
        resolution: quantStep(scale),
        method: 'area-weighted over the texels with data in the region, read from the frame on screen',
        ...(measuredAt ? { frameLabel: measuredAt } : {}),
        ...(deltas && baseline ? { baselineFrame: baseline.frameLabel, changeFromBaseline: deltas } : {}),
        ...(periodAnomalyNow && periodState ? { period: periodState.span, meanChange: periodAnomalyNow.summary.meanChange, meanSigma: periodAnomalyNow.summary.meanSigma, shareBeyond2Sigma: periodAnomalyNow.summary.shareBeyond2 } : {}),
        summary: `${label}: mean ${recNum(stats.mean)} ${u}, min ${recNum(stats.min)}, max ${recNum(stats.max)}, σ ${recNum(stats.stdDev)}`.replace(/ ,/g, ','),
      },
    })
  }
  renderBaselineBlock(baselineHost, src, measuredAt ?? null)
  if (!analysisExportsLocked()) body.appendChild(renderExport(src))
  // AYNI: "Cite this view", beside the export it describes (research tier, slice 1).
  if (src.citeView) {
    const cite = actionButton(t('cite.open'), () => src.citeView?.(cite))
    cite.classList.add('analyze-cite')
    body.appendChild(cite)
  }
  // AYNI: area over a threshold, through time (research tier, S7; `thresholdUI.ts`), over these cells.
  const threshold = document.createElement('section')
  threshold.className = 'analyze-threshold'
  body.appendChild(threshold)
  renderThresholdSection(threshold, {
    src,
    frame,
    window: window ?? undefined,
    regionKey: lastRegionKey,
    regionLabel: root?.querySelector<HTMLSelectElement>('.analyze-scope-select')?.selectedOptions[0]?.textContent ?? scopeToValue(scope),
    regionEnglish: scope.kind === 'box' && scope.label ? scope.label : scopeEnglish(scopeToValue(scope)),
    regionBounds: scoped.kind === 'box' ? scoped.bounds : null,
    p90: stats.p90,
  })
  // AYNI (owner, 2026-10-18): the region through time, over the same cells: when it was highest and lowest.
  const throughTime = document.createElement('section')
  throughTime.className = 'analyze-region-series'
  body.appendChild(throughTime)
  renderRegionSeriesSection(throughTime, {
    src,
    frame,
    window: window ?? undefined,
    regionKey: lastRegionKey,
    regionLabel: root?.querySelector<HTMLSelectElement>('.analyze-scope-select')?.selectedOptions[0]?.textContent ?? scopeLabel(scope),
    regionEnglish: scope.kind === 'box' && scope.label ? scope.label : scopeEnglish(scopeToValue(scope)),
    regionBounds: scoped.kind === 'box' ? scoped.bounds : null,
    regionShape: scoped.kind === 'box' && scoped.country ? COUNTRIES_SOURCE : null,
  })
}

/**
 * The zonal block: the field's shape against latitude.
 *
 * No control and no picking — unlike the transect, this needs nothing
 * from the user, because the axis it reduces along is already chosen by
 * the region. One extra pass over the same window the statistics above
 * used, on a surface whose every number already costs a full-frame
 * readback.
 *
 * Latitude is the one axis of a global field that is never arbitrary:
 * insolation, circulation and the fields that follow them are organised
 * by it. Which is why this is the summary worth showing unprompted and
 * the transect is the one worth asking for.
 */
function renderZonalSection(
  host: HTMLElement,
  frame: { snapshot: LumaSnapshot; scale: DisplayColorScale; options: DatasetOverlayOptions },
  window: TexelWindow | undefined,
): void {
  const { snapshot, scale, options } = frame
  const head = document.createElement('div')
  head.className = 'analyze-zonal-head'
  const label = document.createElement('h3')
  label.textContent = t('analyze.zonal.title')
  head.appendChild(label)
  host.appendChild(head)

  const samples = zonalMeans(snapshot, scale, options, window)
  const withData = samples.reduce((n, s) => n + (s.mean == null ? 0 : 1), 0)
  // Two bands is the minimum that can be a line rather than a point. A
  // region one texel tall is a legitimate pick — a thin equatorial strip
  // — and saying so is better than drawing an empty axis.
  if (withData < 2) {
    host.appendChild(message(t('analyze.zonal.narrow')))
    return
  }
  lastZonal = { samples, scale }

  // AYNI: the held baseline's profile, when it covers these rows; for a
  // period, the climatology's and the difference over the cells both have.
  const periodRows = lastFit === 'applies' && periodState?.clim ? periodAnomalyNow?.summary.rows ?? null : null
  const heldZonal: ZonalSample[] | null = lastFit !== 'applies'
    ? null
    : periodState?.clim
      ? periodRows && periodRows.length === samples.length ? periodRows.map((r) => ({ lat: r.lat, mean: r.clim, count: r.count })) : null
      : baseline?.zonal ? [...baseline.zonal] : null
  const zonalDiff = periodRows && heldZonal
    ? periodRows.map((r) => ({ lat: r.lat, delta: r.now === null || r.clim === null ? null : r.now - r.clim }))
    : heldZonal ? zonalDifference(samples, heldZonal) : null
  host.appendChild(renderZonalChart(samples, scale, source?.display() ?? DEFAULT_DISPLAY, zonalDiff && heldZonal ? heldZonal : undefined))
  host.appendChild(caption(t('analyze.zonal.caption')))
  if (zonalDiff) host.appendChild(caption(t(periodRows ? 'research.period.zonalCaption' : 'research.baseline.zonalCaption')))

  // The peak band, which is the question a zonal profile is usually
  // being asked: not how much there is, but *where* it is. Reported as
  // the band's own latitude rather than interpolated between bands —
  // the profile has the resolution of the grid, and no more.
  let peak = samples[0]
  for (const s of samples) {
    if (s.mean != null && (peak.mean == null || s.mean > peak.mean)) peak = s
  }
  const grid = document.createElement('div')
  grid.className = 'analyze-stats'
  grid.appendChild(renderStatTile(t('analyze.zonal.stat.peak'), formatLatitude(peak.lat)))
  grid.appendChild(
    renderStatTile(
      t('analyze.zonal.stat.peakValue'),
      peak.mean == null ? t('analyze.stat.none') : formatStatValue(peak.mean, scale.units),
    ),
  )
  // AYNI: where the profile moved most against the baseline.
  const largest = zonalDiff ? largestZonalChange(zonalDiff) : null
  if (largest && largest.delta !== null) {
    grid.appendChild(
      renderStatTile(
        t('research.baseline.zonalLargest'),
        `${formatLatitude(largest.lat)} · ${formatDelta(largest.delta, scale.units, quantStep(scale), 'mean')}`,
      ),
    )
  }
  host.appendChild(grid)

  host.appendChild(
    coverageNote(
      t('analyze.zonal.coverage', {
        withData: formatNumber(withData),
        rows: formatNumber(samples.length),
      }),
    ),
  )

  const exportBtn = actionButton(t('analyze.zonal.export'), () => {
    const part = zonalCsv()
    if (part) downloadCsv(part.name, part.text)
  })
  exportBtn.className = 'analyze-export'
  if (!analysisExportsLocked()) host.appendChild(exportBtn)
}

/** `12.3°N`. The same *shape* `browseUI` and `chatUI` use for a bare
 *  latitude — one decimal, degree sign, hemisphere letter — but built
 *  with `formatNumber` where they use `toFixed`, so the decimal
 *  separator follows the locale instead of always being a point. Not
 *  formatting parity with them; deliberately a little better. */
function formatLatitude(lat: number): string {
  // i18n-exempt: compass hemisphere letters, same treatment as browseUI
  return `${formatNumber(Math.abs(lat), { maximumFractionDigits: 1 })}°${lat >= 0 ? 'N' : 'S'}`
}

/**
 * The transect block: a control, and a profile once there is one.
 *
 * Redrawn on its own rather than through `refresh`, because a drag
 * changes only this and `refresh` would recompute the histogram and
 * every region statistic above it on each pointer frame.
 */
function renderTransectSection(): void {
  drawTransectSection()
  renderHovSection()
}

/** AYNI: the Line over time under the line (`hovmollerUI.ts`): it records as the frames play. */
function renderHovSection(): void {
  if (!hovHost) return
  const src = source
  renderHovmollerSection(hovHost, hovLine, {
    feed: src?.hovmoller?.() ?? null,
    datasetTitle: () => src?.datasetTitle() ?? null,
    display: () => src?.display() ?? DEFAULT_DISPLAY,
    provenance: () => src?.spanProvenance?.() ?? src?.provenance?.() ?? null,
  })
}

/** AYNI (owner, 2026-10-18): the way the next Line is drawn: a great circle, or along a latitude. */
let linePathMode: LinePath = 'great-circle'

/** AYNI: the latitudes a line along a parallel is most often wanted on (degrees; the tropics and polar circles of 2026). */
export const LINE_LATITUDES = {
  equator: 0,
  cancer: 23.44,
  capricorn: -23.44,
  arctic: 66.56,
  antarctic: -66.56,
} as const

const LINE_LATITUDE_LABEL: Record<keyof typeof LINE_LATITUDES, MessageKey> = {
  equator: 'analyze.line.lat.equator',
  cancer: 'analyze.line.lat.cancer',
  capricorn: 'analyze.line.lat.capricorn',
  arctic: 'analyze.line.lat.arctic',
  antarctic: 'analyze.line.lat.antarctic',
}

/** Put the Line on the whole parallel at this latitude (−180° to 180°), as if drawn. */
function setParallelLine(picker: TransectPicker, lat: number): void {
  const ends: TransectEndpoints = { from: { lat, lon: -180 }, to: { lat, lon: 180 }, path: 'parallel' }
  setHovmollerByAssistant(false)
  clearTransect()
  const onChange = (e: TransectEndpoints | null): void => {
    transectEnds = e
    transectArmed = false
    renderTransectSection()
  }
  if (picker.set) picker.set(ends, onChange)
  if (!transectEnds) onChange(ends)
}

/**
 * AYNI (owner, 2026-10-18): the Line's path. Along a latitude, a whole
 * parallel can be chosen (the Equator, a tropic, a polar circle, or any
 * latitude typed), or two points drawn: the line then keeps the first
 * point's latitude and runs east to the second.
 */
function linePathControls(picker: TransectPicker): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'analyze-line-path'
  const label = document.createElement('label')
  label.htmlFor = 'analyze-line-path-select'
  label.textContent = t('analyze.line.path')
  const select = document.createElement('select')
  select.id = 'analyze-line-path-select'
  select.className = 'analyze-line-path-select'
  for (const [value, key] of [['great-circle', 'analyze.line.path.greatCircle'], ['parallel', 'analyze.line.path.parallel']] as const) {
    const opt = document.createElement('option')
    opt.value = value
    opt.textContent = t(key)
    select.appendChild(opt)
  }
  const current: LinePath = transectEnds ? transectEnds.path ?? 'great-circle' : linePathMode
  select.value = current
  select.addEventListener('change', () => {
    linePathMode = select.value === 'parallel' ? 'parallel' : 'great-circle'
    // A line on the globe follows the new path between the same ends.
    if (transectEnds && picker.set) {
      const ends: TransectEndpoints = linePathMode === 'parallel'
        ? { from: transectEnds.from, to: { lat: transectEnds.from.lat, lon: transectEnds.to.lon }, path: 'parallel' }
        : { from: transectEnds.from, to: transectEnds.to }
      setHovmollerByAssistant(false)
      picker.set(ends, (e) => {
        transectEnds = e
        transectArmed = false
        renderTransectSection()
      })
    } else {
      renderTransectSection()
    }
    transectHost?.querySelector<HTMLElement>('#analyze-line-path-select')?.focus()
  })
  wrap.append(label, select)
  if (current !== 'parallel') return wrap

  const latLabel = document.createElement('label')
  latLabel.htmlFor = 'analyze-line-lat-select'
  latLabel.textContent = t('analyze.line.latitude')
  const lat = document.createElement('select')
  lat.id = 'analyze-line-lat-select'
  lat.className = 'analyze-line-lat-select'
  const none = document.createElement('option')
  none.value = ''
  none.textContent = t('analyze.line.lat.choose')
  lat.appendChild(none)
  for (const id of Object.keys(LINE_LATITUDES) as Array<keyof typeof LINE_LATITUDES>) {
    const opt = document.createElement('option')
    opt.value = id
    opt.textContent = t(LINE_LATITUDE_LABEL[id])
    lat.appendChild(opt)
  }
  const on = transectEnds?.path === 'parallel' && parallelSpanDeg(transectEnds) >= 360 - 1e-6
    ? (Object.keys(LINE_LATITUDES) as Array<keyof typeof LINE_LATITUDES>).find((id) => Math.abs(LINE_LATITUDES[id] - transectEnds!.from.lat) < 1e-6)
    : undefined
  lat.value = on ?? ''
  lat.addEventListener('change', () => {
    const id = lat.value as keyof typeof LINE_LATITUDES
    if (!(id in LINE_LATITUDES)) return
    setParallelLine(picker, LINE_LATITUDES[id])
    transectHost?.querySelector<HTMLElement>('#analyze-line-lat-select')?.focus()
  })
  const typed = document.createElement('input')
  typed.type = 'number'
  typed.min = '-89.9'
  typed.max = '89.9'
  typed.step = 'any'
  typed.inputMode = 'decimal'
  typed.className = 'analyze-line-lat'
  typed.id = 'analyze-line-lat-input'
  typed.placeholder = t('analyze.line.lat.typed')
  typed.setAttribute('aria-label', t('analyze.line.lat.typedAria'))
  if (transectEnds?.path === 'parallel' && !on) typed.value = String(Math.round(transectEnds.from.lat * 100) / 100)
  typed.addEventListener('change', () => {
    const v = Number(typed.value)
    if (typed.value.trim() === '' || !Number.isFinite(v) || Math.abs(v) >= 90) return
    setParallelLine(picker, v)
    transectHost?.querySelector<HTMLElement>('#analyze-line-lat-input')?.focus()
  })
  wrap.append(latLabel, lat, typed)
  return wrap
}

function drawTransectSection(): void {
  hovLine = null
  const host = transectHost
  const picker = source?.transect?.()
  if (!host || !picker) return
  host.replaceChildren()

  const head = document.createElement('div')
  head.className = 'analyze-transect-head'
  const label = document.createElement('h3')
  label.textContent = t('analyze.transect.title')
  head.appendChild(label)
  host.appendChild(head)

  if (!transectArmed) host.appendChild(linePathControls(picker))

  if (transectArmed) {
    head.appendChild(
      actionButton(t('analyze.transect.cancel'), () => {
        clearTransect()
        renderTransectSection()
      }),
    )
    host.appendChild(
      message(
        picker.progress() >= 1
          ? t('analyze.transect.pickSecond')
          : t('analyze.transect.pickFirst'),
      ),
    )
    return
  }

  if (!transectEnds) {
    head.appendChild(
      actionButton(t('analyze.transect.draw'), () => {
        setHovmollerByAssistant(false)
        transectArmed = true
        transectEnds = null
        picker.begin((ends) => {
          transectEnds = ends
          transectArmed = false
          renderTransectSection()
        }, linePathMode)
        renderTransectSection()
      }),
    )
    host.appendChild(message(t('analyze.transect.hint')))
    return
  }

  head.appendChild(
    actionButton(t('analyze.transect.clear'), () => {
      clearTransect()
      renderTransectSection()
    }),
  )

  const frame = lastFrame
  if (!frame) {
    host.appendChild(message(t('analyze.empty.noDataset')))
    return
  }
  const { snapshot, scale, options } = frame
  const samples = sampleLine(snapshot, scale, transectEnds, lineSampleCount(snapshot, transectEnds, options), options)
  hovLine = { samples, scale }
  const summary = summarizeTransect(samples)
  if (!summary) {
    host.appendChild(message(t('analyze.transect.noData')))
    return
  }
  lastTransect = { samples, scale }

  host.appendChild(renderTransectChart(samples, scale, source?.display() ?? DEFAULT_DISPLAY))
  host.appendChild(caption(t('analyze.transect.caption')))

  const grid = document.createElement('div')
  grid.className = 'analyze-stats'
  const u = scale.units
  grid.appendChild(
    renderStatTile(
      t('analyze.transect.stat.length'),
      formatStatValue(summary.lengthKm, t('analyze.units.km')),
    ),
  )
  grid.appendChild(renderStatTile(t('analyze.stat.min'), formatStatValue(summary.min, u)))
  grid.appendChild(renderStatTile(t('analyze.stat.max'), formatStatValue(summary.max, u)))
  grid.appendChild(renderStatTile(t('analyze.stat.mean'), formatStatValue(summary.mean, u)))
  host.appendChild(grid)

  host.appendChild(
    coverageNote(
      t('analyze.transect.coverage', {
        withData: formatNumber(summary.withData),
        samples: formatNumber(summary.samples),
        spacing: formatStatValue(
          summary.samples > 1 ? summary.lengthKm / (summary.samples - 1) : 0,
          t('analyze.units.km'),
        ),
      }),
    ),
  )

  const exportBtn = actionButton(t('analyze.transect.export'), () => {
    const part = transectCsv()
    if (part) downloadCsv(part.name, part.text)
  })
  // The same full-width control the region export uses, not the small
  // inline action in the header row.
  exportBtn.className = 'analyze-export'
  if (!analysisExportsLocked()) host.appendChild(exportBtn)
}

/**
 * The contour block: outline whatever the colorbar's threshold is
 * isolating, and say how much area that is.
 *
 * The threshold is A1's, not one of this panel's own. That makes the
 * line and the colour agree by construction — what the globe is
 * isolating is what gets outlined and what gets measured — at the cost
 * of the contour being tied to a viewing control, so resetting the
 * palette takes it away. That tradeoff was chosen deliberately.
 *
 * Read at Draw time rather than subscribed to, which is this panel's
 * standing doctrine: computation is user-initiated and one-shot. Moving
 * the colorbar afterwards leaves the drawn lines where they were until
 * Draw is pressed again, and the caption says so rather than leaving
 * someone to discover it.
 */
function renderContourSection(): void {
  // Consumed exactly once per render, whichever path this takes. Read
  // here rather than at the draw site so an early return — no levels in
  // scope, no numbers to draw against — cannot leave the request armed
  // to fire on some later, unrelated render.
  const redrawPending = contourRedrawPending
  contourRedrawPending = false

  const host = contourHost
  const overlay = source?.contours?.()
  const src = source
  if (!host || !overlay || !src) return
  host.replaceChildren()

  const head = document.createElement('div')
  head.className = 'analyze-contour-head'
  const label = document.createElement('h3')
  label.textContent = t('analyze.contour.title')
  head.appendChild(label)
  host.appendChild(head)

  const frame = lastFrame
  const result = lastResult
  if (!frame || !result) {
    host.appendChild(message(t('analyze.empty.noValues')))
    return
  }

  const display = src.display()
  const { min, max } = display.threshold
  const { scale } = result

  // Levels are the colour bar's own round-number ticks, not an even
  // division of min..max. Two reasons: a tick lands on 1/2/5 × 10^k
  // rather than on 3.47e-5, which is what every paper contour map does
  // and what a reader can hold in their head; and because they are
  // *the same* ticks the bar is labelled with, a line on the globe can
  // be read against the legend without interpolating between labels.
  //
  // The threshold scopes rather than sets them. With no threshold the
  // contours span the whole range; with one, they subdivide only the
  // band the globe is isolating — which is the colour bar saying which
  // part of the range is worth subdividing.
  const inScope = (v: number): boolean =>
    (min === null || v >= min) && (max === null || v <= max)
  const lut = buildDisplayLut(scale, display)
  const levels = colorbarTicks(scale, display, CONTOUR_LEVEL_TARGET)
    .map(tick => tick.value)
    .filter(v => Number.isFinite(v) && inScope(v))

  if (!levels.length) {
    // Can happen with a narrow threshold band that no round tick falls
    // inside. Say which control moves it rather than showing a dead
    // button.
    host.appendChild(message(t('analyze.contour.noLevels')))
    return
  }

  const draw = (): void => {
    const set = extractContourSet(
      frame.snapshot, frame.scale, levels, frame.options, lastWindow)
    // Each line painted in the colour the globe is already using at that
    // level, so the map and its own contours cannot disagree. A level
    // the ramp hides gets no colour back and falls through to the
    // default rather than being drawn in a colour that appears nowhere
    // on the surface.
    overlay.show(set.map((level): ContourLevel => {
      const color = displayColorAtValue(lut, scale, level.value)
      return color ? { ...level, color } : level
    }))
    contourDrawn = true
    contourStaleCleared = false
    contourFrameId = src.frameId?.() ?? null
    startContourWatch()
  }

  // Redraw before the button is built, so its label reads Clear rather
  // than flashing Draw for one render. Doing it here rather than after
  // `refresh` returns is what keeps this to a single pass: the section
  // is being rebuilt anyway, and `draw` already has every derived piece
  // in scope.
  if (redrawPending) draw()

  head.appendChild(
    actionButton(
      contourDrawn ? t('analyze.contour.clear') : t('analyze.contour.draw'),
      () => {
        if (contourDrawn) clearContours()
        else draw()
        renderContourSection()
      },
    ),
  )

  // Area at each level, from the histogram rather than from the polygons
  // the lines enclose — see `datasetContours`' header for why the counted
  // number is the one to quote. This is the newsroom question asked at
  // every line rather than at one.
  const list = document.createElement('ul')
  list.className = 'analyze-contour-levels'
  for (const value of levels) {
    const row = document.createElement('li')
    const swatch = document.createElement('span')
    swatch.className = 'analyze-contour-swatch'
    const color = displayColorAtValue(lut, scale, value)
    if (color) swatch.style.background = color
    row.appendChild(swatch)
    const text = document.createElement('span')
    text.textContent = t('analyze.contour.levelRow', {
      level: formatStatValue(value, scale.units),
      km2: formatNumber(Math.round(areaAboveKm2(result.hist, scale, value))),
    })
    row.appendChild(text)
    list.appendChild(row)
  }
  host.appendChild(list)

  host.appendChild(
    caption(
      min !== null || max !== null
        ? t('analyze.contour.scopedCaption', { count: formatNumber(levels.length) })
        : t('analyze.contour.rangeCaption', { count: formatNumber(levels.length) }),
    ),
  )
  if (contourStaleCleared) host.appendChild(message(t('analyze.contour.staleCleared')))
  // Drawn, but with no way to notice the globe moving on. Say it on the
  // surface: a guard that cannot run is worth exactly as much as the
  // user's knowledge that it cannot run.
  if (contourDrawn && contourWatch === null) {
    host.appendChild(message(t('analyze.contour.unwatched')))
  }
  host.appendChild(caption(t('analyze.contour.staleNote')))
}

function clearContours(): void {
  source?.contours?.()?.clear()
  contourDrawn = false
  contourFrameId = null
  // The staleness explanation belongs to one draw/watch cycle. Left set,
  // it outlives the lines it was explaining: reopening the panel, or
  // changing region, shows "the globe moved to another frame" beside a
  // section that drew nothing — blaming playback for something else, and
  // pointing at contours that were never on screen. The watch re-sets it
  // on the line after it calls this, so clearing it here costs nothing.
  contourStaleCleared = false
  stopContourWatch()
}

/**
 * How often to ask whether the globe has moved to another frame.
 *
 * A string compare twice a second, and only while lines are actually on
 * the globe. The panel's standing rule is that nothing here runs per
 * frame or per pointer event; this runs at neither rate, and the
 * alternative — a seam from the playback controller into this panel —
 * is a larger change than removing a stale annotation warrants.
 */
const CONTOUR_WATCH_MS = 500

/**
 * Remove the lines once the globe is showing a different frame.
 *
 * Contours are computed from one frame and never recomputed, so on a
 * playing animation they very quickly describe something that is no
 * longer on screen — a confident isoline over a field it was not
 * measured from. Every other annotation this panel draws is cleared when
 * the thing explaining it goes away: the region box on dataset change,
 * the transect on close, all of them on dispose. Playback was the case
 * that got missed.
 *
 * Clearing rather than recomputing is deliberate. Recomputing means a
 * full ~8 MB readback, which `glLumaSampler`'s docstring is emphatic
 * cannot happen per displayed frame. Removing the line costs nothing and
 * is honest; following it live is a separate piece of work with a
 * separate budget.
 */
function startContourWatch(): void {
  stopContourWatch()
  const src = source
  // No way to identify the frame means no way to notice it changing.
  // Say so on the surface rather than running a watch that compares
  // null to null forever and reports nothing.
  if (!src?.frameId || contourFrameId === null) return
  contourWatch = window.setInterval(() => {
    if (!contourDrawn) {
      stopContourWatch()
      return
    }
    const now = src.frameId?.() ?? null
    if (now === contourFrameId) return
    clearContours()
    contourStaleCleared = true
    renderContourSection()
  }, CONTOUR_WATCH_MS)
}

function stopContourWatch(): void {
  if (contourWatch === null) return
  window.clearInterval(contourWatch)
  contourWatch = null
}

function actionButton(text: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'analyze-action'
  btn.textContent = text
  btn.addEventListener('click', onClick)
  return btn
}

function caption(text: string): HTMLElement {
  const p = document.createElement('p')
  p.className = 'analyze-caption'
  p.textContent = text
  return p
}

function coverageNote(text: string): HTMLElement {
  const p = document.createElement('p')
  p.className = 'analyze-coverage'
  p.textContent = text
  return p
}

/**
 * Longitude span past which a box is not worth outlining.
 *
 * "What I can see" on a zoomed-out globe resolves to most of the world,
 * and a ring at the antimeridian and the poles is noise rather than an
 * answer to "where did these numbers come from". Named regions are
 * never this wide.
 */
const OUTLINE_MAX_LON_SPAN = 300

/**
 * Keep the drawn box in step with the picked region.
 *
 * The *requested* region is outlined, not the part of it the dataset
 * covers — including when the region misses the dataset entirely, which
 * is the one case where seeing the box is the whole explanation of the
 * message beside it. Coverage already reports how much of the box
 * carried data, so the two together say more than either alone.
 */
function syncRegionOutline(src: AnalyzeSource, scoped: ScopeBounds): void {
  const outline = src.regionOutline?.()
  if (!outline) return
  if (scoped.kind !== 'box') {
    outline.clear()
    return
  }
  // AYNI (owner, 2026-10-18): a country is outlined by its own shape, whatever its width (Russia spans the globe).
  if (scoped.country && outline.showShape) {
    outline.showShape(countryOutline(scoped.country))
    return
  }
  const { w, e } = scoped.bounds
  const span = w <= e ? e - w : 360 - w + e
  if (span >= OUTLINE_MAX_LON_SPAN) outline.clear()
  else outline.show(scoped.bounds)
}

function message(text: string): HTMLElement {
  const p = document.createElement('p')
  p.className = 'analyze-message'
  p.textContent = text
  return p
}

function renderStats(
  stats: RegionStats,
  /** AYNI: each statistic's difference from a held baseline (research tier). */
  deltas: Record<BaselineStat, number> | null = null,
  step: number | null = null,
): HTMLElement {
  const grid = document.createElement('div')
  grid.className = 'analyze-stats'
  const u = stats.units
  const km2 = t('analyze.units.km2')
  const rows: Array<[BaselineStat, string, string]> = [
    ['mean', t('analyze.stat.mean'), formatStatValue(stats.mean, u)],
    ['median', t('analyze.stat.median'), formatStatValue(stats.median, u)],
    ['min', t('analyze.stat.min'), formatStatValue(stats.min, u)],
    ['max', t('analyze.stat.max'), formatStatValue(stats.max, u)],
    ['p10', t('analyze.stat.p10'), formatStatValue(stats.p10, u)],
    ['p90', t('analyze.stat.p90'), formatStatValue(stats.p90, u)],
    ['stdDev', t('analyze.stat.stdDev'), formatStatValue(stats.stdDev, u)],
    ['areaKm2', t('analyze.stat.area'), formatStatValue(stats.areaKm2, km2)],
  ]
  for (const [key, label, value] of rows) {
    const tile = renderStatTile(label, value)
    if (deltas) {
      const d = document.createElement('span')
      d.className = 'analyze-stat-delta'
      const delta = deltas[key]
      d.textContent = formatDelta(delta, key === 'areaKm2' ? km2 : u, step, key)
      d.dataset.sign = withinResolution(key, delta, step) || delta === 0 ? '0' : delta > 0 ? '+' : '-'
      tile.appendChild(d)
    }
    grid.appendChild(tile)
  }
  return grid
}

/** AYNI: one quantisation step in the scale's units, or null for a scale
 *  where a step is not one width (a log or classified release). */
export function quantStep(scale: DisplayColorScale): number | null {
  // AYNI: the publisher's step, not a decoded code's (value error audit, 2026-10-19).
  return scaleCodeStep(scale)
}

/** AYNI: a difference from the baseline, signed, or "within resolution". */
function formatDelta(delta: number, units: string | undefined, step: number | null, stat: BaselineStat): string {
  if (delta === 0) return t('research.baseline.noChange')
  if (withinResolution(stat, delta, step)) return t('research.baseline.withinStep')
  const sign = delta > 0 ? '+' : '−'
  return t('research.baseline.delta', { value: `${sign}${formatStatValue(Math.abs(delta), units)}` })
}

/** AYNI: the baseline for a CSV, when it applies to what is being exported. */
function csvBaseline(): CsvBaseline | undefined {
  if (lastFit !== 'applies') return undefined
  // AYNI: a period: its month's statistics, which years, how many frames, and how.
  const state = periodState
  if (state?.clim) {
    const now = periodAnomalyNow
    if (!now) return undefined
    const clim = state.clim
    const cal = state.calendar
    const group = groupName(cal, now.group, true)
    const visited = expectedGroups(cal)
    const years = visited.map((g) => `${groupName(cal, g, true) ?? 'all'}: ${clim.yearsPerGroup[g]}`).join('; ') // i18n-exempt: CSV row
    const values = visited.map((g) => clim.framesPerGroup[g])
    return {
      frameLabel: group ? `${state.span} climatology, ${group}` : `${state.span} mean`, // i18n-exempt: CSV row, English like every CSV row
      datasetTitle: state.datasetTitle,
      zonal: now.summary.rows.map((r) => ({ lat: r.lat, mean: r.clim, count: r.count })),
      zonalDifference: now.summary.rows.map((r) => (r.now === null || r.clim === null ? null : r.now - r.clim)),
      period: {
        span: state.span,
        framesRead: `${state.run.read} of ${state.run.total}`, // i18n-exempt: CSV row
        framesSkipped: state.run.check.skipped,
        grouping: cal.grouping,
        yearsPerGroup: years,
        cellFactor: clim.factor,
        sigmaFlooredShare: clim.cellsWithSigma ? clim.flooredCells / clim.cellsWithSigma : null,
        sigmaDof: `${Math.max(0, Math.min(...values) - 2)} to ${Math.max(0, Math.max(...values) - 2)}`, // i18n-exempt: CSV row
        method: periodMethodText(cal.grouping),
      },
      anomaly: {
        meanChange: now.summary.meanChange,
        meanSigma: now.summary.meanSigma,
        shareBeyond2: now.summary.shareBeyond2,
        areaKm2: now.summary.areaKm2,
        cells: now.summary.cells,
      },
    }
  }
  if (!baseline) return undefined
  return { frameLabel: baseline.frameLabel, datasetTitle: baseline.datasetTitle, stats: baseline.stats, zonal: baseline.zonal }
}

/**
 * AYNI: the baseline controls under the statistics (research tier, slice 2):
 * hold this frame, hold it instead, or clear; and what the baseline is and
 * whether it applies here. A plan with the data tools but not the research
 * tools sees the button with a lock that opens the plans; a class borrowing
 * the data tools sees nothing (no upsell to students).
 */
function renderBaselineBlock(body: HTMLElement, src: AnalyzeSource, measuredAt: string | null): void {
  const wrap = document.createElement('div')
  wrap.className = 'analyze-baseline'
  const rerender = (focusSelector: string) => {
    const host = root?.querySelector('.analyze-body') as HTMLElement | null
    if (!host) return
    refresh(host)
    ;(host.querySelector(focusSelector) as HTMLElement | null)?.focus()
  }
  if (!anomaliesAllowed()) {
    if (!researchUpsellShown()) return
    const locked = actionButton(t('research.baseline.hold'), () => {
      openPlans(locked, 'research', { notice: t('plans.locked.research') })
    })
    locked.classList.add('analyze-baseline-hold', 'analyze-locked')
    locked.setAttribute('aria-describedby', 'analyze-baseline-lock')
    const why = document.createElement('span')
    why.id = 'analyze-baseline-lock'
    why.className = 'analyze-lock-note'
    why.textContent = t('research.baseline.locked')
    wrap.append(locked, why)
    body.appendChild(wrap)
    return
  }
  // AYNI: one frame, or a period (S2): which controls the tab shows.
  wrap.appendChild(segmentedChoice<BaselineKind>(
    'analyze-baseline-kind', t('research.baseline.kind'), t('research.baseline.kindAria'),
    [['frame', t('research.baseline.kindFrame')], ['period', t('research.baseline.kindPeriod')]],
    baselineKind,
    (kind) => {
      baselineKind = kind
      rerender(`.analyze-baseline-kind[data-value="${kind}"]`)
    },
  ))
  if (baselineKind === 'period') {
    renderPeriodBlock(wrap, src, rerender)
    body.appendChild(wrap)
    if (periodState?.field && lastFit === 'applies') body.appendChild(renderChangeBlock(src, rerender))
    return
  }
  if (periodState) wrap.appendChild(caption(t('research.period.heldInstead', { span: periodState.span })))
  if (baseline) {
    const note = document.createElement('p')
    note.className = 'analyze-baseline-note'
    const frame = baseline.frameLabel ?? t('research.baseline.noFrame')
    if (lastFit === 'applies') {
      note.textContent = t('research.baseline.comparing', { frame, region: baseline.regionLabel })
      if (baseline.datasetId !== src.datasetId() && baseline.datasetTitle) {
        note.textContent += ` ${t('research.baseline.otherDataset', { title: baseline.datasetTitle })}`
      }
    } else if (lastFit === 'other-units') {
      note.textContent = t('research.baseline.otherUnits', { frame, units: baseline.units ?? '—' })
    } else {
      note.textContent = t('research.baseline.otherRegion', { frame, region: baseline.regionLabel })
    }
    wrap.appendChild(note)
  }
  const hold = actionButton(t(baseline ? 'research.baseline.holdInstead' : 'research.baseline.hold'), () => {
    if (!lastResult) return
    // AYNI: one baseline at a time: a frame replaces a period.
    clearPeriod()
    const select = root?.querySelector<HTMLSelectElement>('.analyze-scope-select')
    baseline = {
      datasetId: src.datasetId() ?? '',
      datasetTitle: src.datasetTitle(),
      regionKey: lastRegionKey,
      regionLabel: select?.selectedOptions[0]?.textContent ?? scopeToValue(scope),
      frameLabel: measuredAt,
      units: lastResult.stats.units,
      stats: lastResult.stats,
      zonal: lastZonal ? [...lastZonal.samples] : null,
      field: lastFrame
        ? {
            data: new Uint8Array(lastFrame.snapshot.data),
            width: lastFrame.snapshot.width,
            height: lastFrame.snapshot.height,
            scale: lastFrame.scale,
            gridKey: gridKey(lastFrame.snapshot.width, lastFrame.snapshot.height, lastFrame.options),
          }
        : undefined,
    }
    heldLab = { for: baseline, query: src.viewQuery?.() ?? '', region: labRegionOf(scope) }
    // A new baseline (perhaps a smaller grid) gets another try on the graphics card.
    liveRefused = false
    // AYNI: a research record, when one is recording, notes what was held (slice 4).
    trail({
      type: 'baseline',
      frame: true,
      data: {
        kind: 'frame',
        dataset: baseline.datasetId,
        datasetTitle: baseline.datasetTitle,
        frameLabel: measuredAt,
        region: baseline.regionLabel,
        units: baseline.units ?? '',
        mean: baseline.stats.mean, min: baseline.stats.min, max: baseline.stats.max, stdDev: baseline.stats.stdDev,
        summary: `One frame (${measuredAt ?? 'no date'}) over ${baseline.regionLabel}: mean ${recNum(baseline.stats.mean)} ${baseline.units ?? ''}`.trimEnd(),
      },
    })
    // The readers (pointer, pins) take the same frame.
    const held = baseline.field
    setChangeView({
      field: held
        ? { ...held, options: lastFrame?.options, frameLabel: measuredAt, units: lastResult.stats.units }
        : null,
    })
    rerender('.analyze-baseline-clear')
  })
  hold.classList.add('analyze-baseline-hold')
  wrap.appendChild(hold)
  if (baseline) {
    const clear = actionButton(t('research.baseline.clear'), () => {
      trail({ type: 'baseline', data: { kind: 'cleared', summary: 'Baseline cleared' } })
      baseline = null
      setChangeView({ mode: null, field: null })
      rerender('.analyze-baseline-hold')
    })
    clear.classList.add('analyze-baseline-clear')
    wrap.appendChild(clear)
  }
  body.appendChild(wrap)
  if (baseline?.field) body.appendChild(renderChangeBlock(src, rerender))
}

/** AYNI: a row of pressed/unpressed buttons (one choice), each re-rendering the panel. */
function segmentedChoice<T extends string>(
  cls: string,
  labelText: string,
  aria: string,
  options: Array<[T, string]>,
  current: T,
  pick: (v: T) => void,
): HTMLElement {
  const group = document.createElement('div')
  group.className = 'analyze-change-modes'
  group.setAttribute('role', 'group')
  group.setAttribute('aria-label', aria)
  const label = document.createElement('span')
  label.className = 'analyze-change-label'
  label.textContent = labelText
  group.appendChild(label)
  for (const [value, text] of options) {
    const b = actionButton(text, () => pick(value))
    b.classList.add('analyze-change-mode', cls)
    b.dataset.value = value
    b.setAttribute('aria-pressed', String(current === value))
    group.appendChild(b)
  }
  return group
}

/**
 * AYNI: a period as the baseline (research tier, S2): the years, how the
 * frames are grouped, what σ is possible, and then "Start reading", the
 * frames read so far with a way to read the missing ones (and to finish
 * once every group has 80 % of its frames), or what the comparison is
 * against. Nothing is compared against a period still being read.
 */
function renderPeriodBlock(wrap: HTMLElement, src: AnalyzeSource, rerender: (focusSelector: string) => void): void {
  const box = document.createElement('div')
  box.className = 'analyze-period'
  wrap.appendChild(box)
  const state = periodState
  const axis = src.timeAxis?.() ?? null
  const clear = (): HTMLButtonElement => {
    const b = actionButton(t('research.baseline.clear'), () => {
      clearPeriod()
      rerender('.analyze-period-start')
    })
    b.classList.add('analyze-baseline-clear')
    return b
  }
  const howKey = (cal: ClimateCalendar): MessageKey =>
    cal.grouping === 'monthly'
      ? 'research.period.howMonthly'
      : cal.grouping === 'annual'
        ? 'research.period.howAnnual'
        : cal.grouping === 'daily'
          ? 'research.period.howDaily'
          : cal.grouping === 'subdaily'
            ? 'research.period.howSubdaily'
            : cal.windowReason === 'tooFine' ? 'research.period.howTooFine' : 'research.period.howWindow'
  const skippedNote = (skipped: number) => {
    if (skipped > 0) box.appendChild(caption(t('research.period.skipped', { count: formatNumber(skipped) })))
  }

  // Complete: what the comparison is against.
  if (state?.clim) {
    const clim = state.clim
    const note = document.createElement('p')
    note.className = 'analyze-baseline-note'
    const frames = { read: formatNumber(state.run.read), total: formatNumber(state.run.total) }
    if (lastFit === 'other-units') note.textContent = t('research.period.otherUnits', { span: state.span, units: state.units ?? '—' })
    else if (lastFit === 'other-grid') note.textContent = t('research.period.otherGrid', { span: state.span })
    else if (lastFit === 'other-calendar') note.textContent = t('research.period.otherCalendar', { span: state.span })
    else {
      const g = state.calendar.grouping
      note.textContent = t(g === 'monthly' || g === 'daily' || g === 'subdaily' ? 'research.period.comparingMonthly' : g === 'annual' ? 'research.period.comparingAnnual' : 'research.period.comparingWindow', { span: state.span, ...frames })
      const group = periodAnomalyNow ? groupName(state.calendar, periodAnomalyNow.group) : null
      if (group) note.textContent += ` ${t('research.period.thisMonth', { month: group })}`
      if (state.datasetId !== src.datasetId() && state.datasetTitle) note.textContent += ` ${t('research.baseline.otherDataset', { title: state.datasetTitle })}`
    }
    box.appendChild(note)
    box.appendChild(caption(periodMethod(state.calendar.grouping)))
    skippedNote(state.run.check.skipped)
    if (state.calendar.grouping !== 'window') {
      const visited = expectedGroups(state.calendar)
      const minYears = Math.min(...visited.map((g) => clim.yearsPerGroup[g]))
      if (minYears < MIN_SIGMA_YEARS) box.appendChild(caption(t('research.period.sigmaTooShort', { years: String(minYears), min: String(MIN_SIGMA_YEARS) })))
      else if (minYears < NORMAL_YEARS) box.appendChild(caption(t('research.period.short', { years: String(minYears) })))
    }
    if (clim.flooredCells > 0 && clim.cellsWithSigma > 0) {
      const share = (100 * clim.flooredCells) / clim.cellsWithSigma
      box.appendChild(caption(t('research.period.floored', {
        count: formatNumber(clim.flooredCells),
        share: formatNumber(share, { maximumFractionDigits: share < 1 ? 1 : 0 }),
      })))
    }
    if (clim.factor > 1) box.appendChild(caption(t('research.period.thinned', { factor: String(clim.factor) })))
    box.appendChild(clear())
    return
  }

  // Being read: how far, and how to finish.
  if (state) {
    const progress = document.createElement('p')
    progress.className = 'analyze-period-progress'
    progress.setAttribute('role', 'status')
    progress.textContent = t('research.period.progress', { read: formatNumber(state.run.read), total: formatNumber(state.run.total) })
    const bar = document.createElement('progress')
    bar.className = 'analyze-period-bar'
    bar.max = state.run.total
    bar.value = state.run.read
    bar.setAttribute('aria-hidden', 'true')
    box.append(progress, bar)
    skippedNote(state.run.check.skipped)
    if (state.datasetId !== src.datasetId()) {
      box.appendChild(caption(t('research.period.paused', { title: state.datasetTitle ?? '—' })))
    } else {
      box.appendChild(caption(t('research.period.playHint', { span: state.span })))
      if (axis) {
        const step = state.stepping
          ? actionButton(t('research.period.stop'), () => {
              state.cancelStep = true
            })
          : actionButton(t('research.period.step'), () => void stepPeriod(axis))
        step.classList.add(state.stepping ? 'analyze-period-stop' : 'analyze-period-step')
        box.appendChild(step)
      }
    }
    // Every group at 80 % (WMO's completeness guideline): the reader may finish here.
    const early = actionButton(t('research.period.useEarly', { read: formatNumber(state.run.read) }), () => finishPeriod(true))
    early.classList.add('analyze-period-early')
    early.hidden = !state.run.enough()
    box.appendChild(early)
    if (state.run.factor > 1) box.appendChild(caption(t('research.period.thinned', { factor: String(state.run.factor) })))
    box.appendChild(clear())
    return
  }

  // Not started: the period, and whether it can be one.
  if (!axis || !lastFrame) {
    box.appendChild(message(t('research.period.noAxis')))
    return
  }
  if ((lastFrame.scale as { release?: { kind?: string } }).release?.kind === 'luma8-classified') {
    box.appendChild(message(t('research.change.classified')))
    return
  }
  const cal = calendarFor(axis)
  const period = cal.grouping === 'window' ? null : (periodChoice ? clampPeriod(axis, periodChoice) : null) ?? defaultPeriod(axis)
  if (period) {
    const years = document.createElement('div')
    years.className = 'analyze-period-years'
    const bounds = clampPeriod(axis, { from: -1e9, to: 1e9 })!
    const input = (cls: string, labelKey: MessageKey, value: number): HTMLLabelElement => {
      const label = document.createElement('label')
      label.className = 'analyze-period-field'
      const text = document.createElement('span')
      text.textContent = t(labelKey)
      const field = document.createElement('input')
      field.type = 'number'
      field.className = cls
      field.min = String(bounds.from)
      field.max = String(bounds.to)
      field.step = '1'
      field.value = String(value)
      field.addEventListener('change', () => {
        const from = Number((years.querySelector('.analyze-period-from') as HTMLInputElement).value)
        const to = Number((years.querySelector('.analyze-period-to') as HTMLInputElement).value)
        const next = Number.isFinite(from) && Number.isFinite(to) ? clampPeriod(axis, { from, to }) ?? period : period
        // Redrawing takes the focused box away, whose blur fires change again: only a new period redraws.
        const was = periodChoice ?? period
        if (next.from === was.from && next.to === was.to) {
          // Out of range and clamped back: show the years in use again.
          field.value = String(cls === 'analyze-period-from' ? was.from : was.to)
          return
        }
        periodChoice = next
        rerender(`.${cls}`)
      })
      label.append(text, field)
      return label
    }
    years.append(input('analyze-period-from', 'research.period.from', period.from), input('analyze-period-to', 'research.period.to', period.to))
    box.appendChild(years)
  }
  const span = periodSpan(axis, period)
  box.appendChild(caption(t(howKey(cal), { span })))
  // 1991–2020 only partly on the axis, though the axis has 30 years: say a full normal is possible.
  if (period && period.from >= STANDARD_PERIOD.from && period.to <= STANDARD_PERIOD.to) {
    const all = axisYears(axis)
    const overlap = Math.min(STANDARD_PERIOD.to, all.to) - Math.max(STANDARD_PERIOD.from, all.from) + 1
    const total = all.to - all.from + 1
    if (overlap >= MIN_SIGMA_YEARS && overlap < NORMAL_YEARS && total >= NORMAL_YEARS) {
      const from = all.from > STANDARD_PERIOD.from ? all.from : all.to - NORMAL_YEARS + 1
      box.appendChild(caption(t('research.period.overlap', { overlap: String(overlap), total: String(total), suggest: t('research.period.span', { from: String(from), to: String(from + NORMAL_YEARS - 1) }) })))
    }
  }
  const check = checkPeriod(periodFrames(axis, cal, period, axis.usable), cal)
  if (check.refusal) {
    box.appendChild(message(t(check.refusal === 'tooMany' ? 'research.period.tooMany' : 'research.period.tooFew')))
    return
  }
  if (check.sigma === 'tooShort') box.appendChild(caption(t('research.period.sigmaTooShort', { years: String(check.minYears), min: String(MIN_SIGMA_YEARS) })))
  else if (check.sigma === 'short') box.appendChild(caption(t('research.period.short', { years: String(check.minYears) })))
  box.appendChild(caption(t('research.period.frames', { total: formatNumber(check.frames) })))
  skippedNote(check.skipped)
  const start = actionButton(t('research.period.start'), () => {
    if (!lastFrame) return
    if (startPeriod(src, axis, period, lastFrame.scale)) rerender('.analyze-period-step')
  })
  start.classList.add('analyze-period-start')
  box.appendChild(start)
}

/**
 * AYNI: the frame against the period's climatology over the region on
 * screen (S2), under the statistics: place by place where both have data,
 * each place against the mean of its own month. A frame's extremes and
 * percentiles are not compared with a field of means.
 */
function renderPeriodAnomaly(now: { summary: AnomalySummary; group: number }, state: PeriodState, units: string | undefined, step: number | null): HTMLElement {
  const wrap = document.createElement('section')
  wrap.className = 'analyze-period-anomaly'
  const head = document.createElement('h3')
  const group = groupName(state.calendar, now.group)
  head.textContent = group ? t('research.period.anomalyTitle', { span: state.span, group }) : t('research.period.anomalyTitlePlain', { span: state.span })
  wrap.appendChild(head)
  const tiles = document.createElement('div')
  tiles.className = 'analyze-stats'
  const s = now.summary
  const change = renderStatTile(t('research.period.meanChange'), formatDelta(s.meanChange, units, step, 'mean'))
  change.classList.add('analyze-period-mean-change')
  tiles.appendChild(change)
  if (s.meanSigma !== null) {
    const z = s.meanSigma
    tiles.appendChild(renderStatTile(t('research.period.meanSigma'), `${z > 0 ? '+' : z < 0 ? '−' : ''}${formatNumber(Math.abs(z), { maximumFractionDigits: 2 })} σ`)) // i18n-exempt: a number and the σ symbol
  }
  if (s.shareBeyond2 !== null) {
    tiles.appendChild(renderStatTile(t('research.period.beyond2'), `${formatNumber(100 * s.shareBeyond2, { maximumFractionDigits: 1 })} %`)) // i18n-exempt: a number and the % symbol
  }
  tiles.appendChild(renderStatTile(t('research.period.compared'), formatStatValue(s.areaKm2, t('analyze.units.km2'))))
  wrap.appendChild(tiles)
  wrap.appendChild(caption(t('research.period.anomalyCaption')))
  return wrap
}

/**
 * AYNI: "On the globe: Off · Change · % change", "Update: While playing ·
 * When paused" and, when drawn, the legend (research tier; owner,
 * 2026-10-05). The map covers the whole frame, whatever region the
 * numbers above cover; it needs the same grid and units as the baseline.
 */
function renderChangeBlock(src: AnalyzeSource, rerender: (focusSelector: string) => void): HTMLElement {
  const view = changeViewState()
  const block = document.createElement('div')
  block.className = 'analyze-change'
  const segmented = <T extends string | null>(cls: string, labelText: string, aria: string, options: Array<[T, string]>, current: T, pick: (v: T) => void) => {
    const group = document.createElement('div')
    group.className = 'analyze-change-modes'
    group.setAttribute('role', 'group')
    group.setAttribute('aria-label', aria)
    const label = document.createElement('span')
    label.className = 'analyze-change-label'
    label.textContent = labelText
    group.appendChild(label)
    for (const [value, text] of options) {
      const b = actionButton(text, () => {
        pick(value)
        rerender(`.${cls}[data-value="${value ?? 'off'}"]`)
      })
      b.classList.add('analyze-change-mode', cls)
      b.dataset.value = value ?? 'off'
      if (cls === 'analyze-change-mode-pick') b.dataset.mode = value ?? 'off'
      b.setAttribute('aria-pressed', String(current === value))
      group.appendChild(b)
    }
    return group
  }
  // AYNI: σ change needs a period with σ (S2); a frame or a short period has none.
  const sigmaOk = !!periodState?.clim?.hasSigma && view.field === periodState.field
  if (view.mode === 'sigma' && !sigmaOk) setChangeView({ mode: null })
  const modes: Array<[ChangeMode | null, string]> = [[null, t('research.change.off')], ['difference', t('research.change.difference')], ['percent', t('research.change.percent')]]
  if (sigmaOk) modes.push(['sigma', t('research.change.sigma')])
  block.appendChild(segmented<ChangeMode | null>(
    'analyze-change-mode-pick', t('research.change.label'), t('research.change.aria'),
    modes,
    changeViewState().mode,
    (mode) => {
      // AYNI: one map on the globe at a time; choosing the baseline's ends a comparison.
      if (changeViewState().other) {
        compareMode = null
        source?.compareActive?.(null)
        setChangeView({ other: null })
      }
      setChangeView({ mode })
      // AYNI: a research record, when one is recording, notes what the globe shows (slice 4).
      const f = changeViewState().field
      const against = f?.period ? `the ${f.period.label} climatology` : `the baseline held at ${f?.frameLabel ?? 'a frame without a date'}`
      const what = mode === 'difference' ? 'change' : mode === 'percent' ? '% change' : 'σ change'
      trail({ type: 'change', data: { mode: mode ?? 'off', against, summary: mode ? `On the globe: ${what} from ${against}` : 'On the globe: values (change off)' } })
    },
  ))
  if (!changeViewState().mode) return block
  block.appendChild(segmented<ChangeUpdate>(
    'analyze-change-update', t('research.change.update'), t('research.change.updateAria'),
    [['live', t('research.change.live')], ['pause', t('research.change.pause')]],
    view.update,
    (update) => {
      // Asking for While playing again tries the graphics card again.
      if (update === 'live') liveRefused = false
      setChangeView({ update })
    },
  ))
  if (view.other) {
    block.appendChild(caption(t('research.compare.onGlobe')))
    return block
  }
  const result = drawChange(src)
  if (typeof result === 'string') {
    block.appendChild(caption(result))
    return block
  }
  block.appendChild(renderChangeLegend(result.grid, lastFrame?.scale.units, result.live, rerender))
  return block
}

/**
 * AYNI: put the change on the globe: every frame on the graphics card when
 * the viewer chose "While playing" and this globe can, else as a picture of
 * this frame that hides while playing. Returns this frame's grid (for the
 * legend's counts) or a reason in words when it cannot be drawn.
 */
function drawChange(src: AnalyzeSource): { grid: ChangeGrid; live: boolean } | string {
  const view = changeViewState()
  // AYNI: a period's climatology (S2).
  if (periodState?.clim && periodState.field && view.field === periodState.field) return drawPeriodChange(src, periodState)
  const field = baseline?.field
  if (!field || !lastFrame || !view.mode) return t('research.change.unavailable')
  if ((baseline?.units ?? '') !== (lastFrame.scale.units ?? '')) return t('research.change.otherUnits')
  // Classes are not amounts: "snow minus rain" means nothing.
  if ((lastFrame.scale as { release?: { kind?: string } }).release?.kind === 'luma8-classified') return t('research.change.classified')
  if (field.gridKey !== gridKey(lastFrame.snapshot.width, lastFrame.snapshot.height, lastFrame.options)) return t('research.change.otherGrid')
  const grid = computeChangeGrid({
    now: lastFrame.snapshot,
    base: { data: field.data, width: field.width, height: field.height },
    nowScale: lastFrame.scale,
    baseScale: field.scale,
    options: lastFrame.options,
    mode: view.mode,
    step: quantStep(field.scale as DisplayColorScale),
    // One colour, one amount: the range is fixed from the first frame drawn in this mode.
    limit: view.limit,
    // AYNI (owner, 2026-10-20): or the viewer's own ends.
    range: view.range,
    floor: view.floor,
  })
  if (!grid) return t('research.change.otherGrid')
  if (view.limit === null || view.floor !== grid.floor) setChangeView({ limit: grid.limit, floor: grid.floor })
  if (typeof document === 'undefined') return { grid, live: false }

  let live = false
  if (view.update === 'live' && src.liveChange && !liveRefused) {
    const key = [view.mode, grid.range.min, grid.range.max, view.colours.palette, view.colours.inverted, grid.floor, field.gridKey, lastFrame.scale.vmin, lastFrame.scale.vmax, field.data.length].join('|')
    if (liveCurrent(src, key)) {
      live = true
    } else {
      const lut = buildChangeLut(lastFrame.scale, field.scale, view.mode, grid.floor, grid.range, 255, view.colours)
      live = src.liveChange({ base: { data: field.data, width: field.width, height: field.height }, lut })
      if (live) liveSent(src, key)
      else liveRefused = true
    }
  }
  if (live) {
    src.showChange?.(null)
  } else {
    if (liveKey !== null) src.liveChange?.(null)
    liveKey = null
    const canvas = document.createElement('canvas')
    canvas.width = grid.width
    canvas.height = grid.height
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.putImageData(new ImageData(paintChangeGrid(grid, 235, undefined, view.colours), grid.width, grid.height), 0, 0)
    src.showChange?.({ canvas, frameKey: src.frameKey?.() ?? null })
  }
  changeDrawn = true
  setChangeView({ shown: true })
  return { grid, live }
}

/**
 * AYNI: the change from a period's climatology on the globe: each frame
 * against the mean (and σ) of its own calendar month. Live, the graphics
 * card gets the climatology once (16-bit mean and σ per cell and month),
 * each code's value, the palette in 1024 steps and the mode, and picks the
 * month of each frame it draws; paused, the same numbers paint a picture.
 */
function drawPeriodChange(src: AnalyzeSource, state: PeriodState): { grid: ChangeGrid; live: boolean } | string {
  const view = changeViewState()
  const clim = state.clim!
  if (!lastFrame || !view.mode) return t('research.change.unavailable')
  if ((state.units ?? '') !== (lastFrame.scale.units ?? '')) return t('research.change.otherUnits')
  if ((lastFrame.scale as { release?: { kind?: string } }).release?.kind === 'luma8-classified') return t('research.change.classified')
  if (state.gridKey !== gridKey(lastFrame.snapshot.width, lastFrame.snapshot.height, lastFrame.options)) return t('research.change.otherGrid')
  if (view.mode === 'sigma' && !clim.hasSigma) return t('research.period.noSigma', { min: String(MIN_SIGMA_YEARS) })
  const cal = state.calendar
  const group = groupNow(cal)
  // A frame that cannot be placed in the climatology's calendar is not compared (never against January by default).
  if (group === null) return t('research.period.otherCalendar', { span: state.span })
  const grid = computePeriodChangeGrid({
    now: lastFrame.snapshot,
    nowScale: lastFrame.scale,
    clim,
    group,
    options: lastFrame.options,
    mode: view.mode,
    step: quantStep(state.scale),
    limit: view.limit,
    range: view.range,
    floor: view.floor,
  })
  if (!grid) return t('research.change.otherGrid')
  if (view.limit === null || view.floor !== grid.floor) setChangeView({ limit: grid.limit, floor: grid.floor })
  if (typeof document === 'undefined') return { grid, live: false }

  let live = false
  if (view.update === 'live' && src.liveChange && !liveRefused) {
    const key = ['period', state.span, view.mode, grid.range.min, grid.range.max, view.colours.palette, view.colours.inverted, grid.floor, state.gridKey, lastFrame.scale.vmin, lastFrame.scale.vmax, clim.q.length].join('|')
    if (liveCurrent(src, key)) {
      live = true
    } else {
      // Row 0: the frame's codes; row 1: the climatology's.
      const values = new Float32Array(1024)
      for (const [row, table] of [[0, valueTable(lastFrame.scale)], [1, clim.scale.values]] as const) {
        for (let c = 0; c < 256; c++) {
          const finite = Number.isFinite(table[c])
          values[(row * 256 + c) * 2] = finite ? table[c] : 0
          values[(row * 256 + c) * 2 + 1] = finite ? 1 : 0
        }
      }
      live = src.liveChange({
        period: {
          q: clim.q,
          cw: clim.cw,
          ch: clim.ch,
          layers: clim.groups,
          width: clim.width,
          height: clim.height,
          factor: clim.factor,
          codes: [clim.scale.first, clim.scale.last],
          values,
          palette: buildChangePalette(PALETTE_STEPS, view.colours),
          mode: view.mode === 'percent' ? 1 : view.mode === 'sigma' ? 2 : 0,
          limit: grid.range.max,
          lower: -grid.range.min,
          floor: grid.floor ?? 0,
          grey: [CHANGE_GREY[0] / 255, CHANGE_GREY[1] / 255, CHANGE_GREY[2] / 255, 1],
          // The group of the frame the graphics card holds, by its index on the axis.
          layerAt: (time) => {
            const axis = source?.timeAxis?.() ?? null
            if (!axis) return null
            const frame = time === null ? axis.currentFrame() : 'mediaTime' in time ? axis.frameAtMediaTime(time.mediaTime) : axis.frameAtVideoTime(time.currentTime)
            return groupOfFrame(cal, frame)
          },
        },
      })
      if (live) liveSent(src, key)
      else liveRefused = true
    }
  }
  if (live) {
    src.showChange?.(null)
  } else {
    if (liveKey !== null) src.liveChange?.(null)
    liveKey = null
    const canvas = document.createElement('canvas')
    canvas.width = grid.width
    canvas.height = grid.height
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.putImageData(new ImageData(paintChangeGrid(grid, 235, buildChangePalette(PALETTE_STEPS, view.colours)), grid.width, grid.height), 0, 0)
    src.showChange?.({ canvas, frameKey: src.frameKey?.() ?? null })
  }
  changeDrawn = true
  setChangeView({ shown: true })
  return { grid, live }
}

/** AYNI: the change's legend in the panel: the shared bar, then what was greyed and how it updates. */
function renderChangeLegend(grid: ChangeGrid, units: string | undefined, live: boolean, rerender: (focusSelector: string) => void): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'analyze-change-legend'
  const legend = buildChangeLegend({ mode: grid.mode, limit: grid.range.max, min: grid.range.min, units, colours: changeViewState().colours, frameLabel: periodState?.field && changeViewState().field === periodState.field ? periodState.field.frameLabel : undefined })
  wrap.appendChild(withColoursButton(legend, changeColoursButton(grid, units, BASELINE_RANGE)))
  wrap.appendChild(renderChangeRange(grid, units, rerender, BASELINE_RANGE))
  // A field with an arbitrary zero (an anomaly, °C) gets the caution that
  // says why; a ratio field only when much of its baseline is near zero.
  const caution = grid.mode === 'percent' && lastFrame ? percentRefusal(lastFrame.scale) : null
  if (caution || percentMisleads(grid)) {
    const hint = document.createElement('p')
    hint.className = 'analyze-change-hint'
    hint.textContent = t(caution === 'negative' ? 'research.change.percentNegative' : caution === 'interval' ? 'research.change.percentInterval' : 'research.change.percentMisleads')
    wrap.appendChild(hint)
  }
  const greyKey = document.createElement('p')
  greyKey.className = 'analyze-change-grey'
  const swatch = document.createElement('span')
  swatch.className = 'analyze-change-swatch'
  swatch.style.background = `rgb(${CHANGE_GREY.join(', ')})`
  swatch.setAttribute('aria-hidden', 'true')
  const period = !!periodState?.field && changeViewState().field === periodState.field
  const greyText: MessageKey = grid.mode === 'sigma'
    ? 'research.change.greySigma'
    : period
      ? (grid.mode === 'percent' ? 'research.change.greyPeriodPercent' : 'research.change.greyPeriod')
      : (grid.mode === 'percent' ? 'research.change.greyPercent' : 'research.change.grey')
  greyKey.append(swatch, document.createTextNode(t(greyText)))
  wrap.appendChild(greyKey)
  // The exact threshold, and which rule set it (owner, 2026-10-06).
  if (grid.mode === 'percent' && grid.floor !== null) {
    const rule = floorRule(grid.floor, period && periodState ? quantStep(periodState.scale) : baseline?.field ? quantStep(baseline.field.scale as DisplayColorScale) : null)
    const total = grid.painted + grid.masked
    wrap.appendChild(caption(t('research.change.masked', {
      floor: formatStatValue(grid.floor, units),
      count: formatNumber(grid.masked),
      share: formatNumber(total > 0 ? (100 * grid.masked) / total : 0, { maximumFractionDigits: total > 0 && grid.masked / total < 0.01 ? 1 : 0 }),
      rule: rule.rule === 'steps'
        ? t('research.change.floorSteps', { step: formatStatValue(rule.step, units) })
        : t('research.change.floorTypical', { typical: formatStatValue(rule.typical, units) }),
    })))
  }
  // AYNI: what a σ means, at the fixed ±3σ range (not the viewer's own).
  if (grid.mode === 'sigma' && periodState?.clim && !validRange(changeViewState().range)) {
    const clim = periodState.clim
    const years = Math.min(...expectedGroups(periodState.calendar).map((g) => clim.yearsPerGroup[g]))
    wrap.appendChild(caption(t('research.change.sigmaNote', { years: String(years) })))
  }
  const update = changeViewState().update
  wrap.appendChild(caption(t(live ? 'research.change.liveNote' : update === 'live' ? 'research.change.liveRefused' : 'research.change.frameOnly')))
  return wrap
}

/** AYNI: where a change map's colour range is kept: the baseline's (the shared view) or Compare's (its pair). */
interface RangeTarget {
  /** The range drawn now; null before the first frame. */
  shown(): ChangeRange | null
  /** Whether the viewer set it. */
  own(): boolean
  /** The viewer's range, or null for the automatic one. */
  set(range: ChangeRange | null): void
}

const BASELINE_RANGE: RangeTarget = {
  shown: () => shownRange(),
  own: () => validRange(changeViewState().range),
  set: (range) => setChangeView({ range }),
}

/** The units a change map's range is typed in. */
function changeUnitText(grid: ChangeGrid, units: string | undefined): string {
  return grid.mode === 'percent' ? '%' : grid.mode === 'sigma' ? 'σ' : (units ?? '')
}

/** AYNI: the legend with its colour button left of the bar (the ticks keep under the bar). */
function withColoursButton(legend: HTMLElement, button: HTMLButtonElement): HTMLElement {
  const bar = legend.querySelector('.change-legend-bar')
  if (!bar) return legend
  const row = document.createElement('div')
  row.className = 'analyze-change-barrow'
  bar.replaceWith(row)
  row.append(button, bar)
  legend.classList.add('analyze-change-legend-coloured')
  return legend
}

/**
 * AYNI (owner, 2026-10-20: "add a way to change the color palette, maybe
 * reuse the dialog we use when the button to the left of the progress bar
 * of a playing dataset is clicked"): the change map's colour button, left
 * of its bar, opening that same dialog (`overlayPaletteDialog.ts`) with the
 * change's palettes (Grayscale left out: its middle is the grey of "no
 * meaningful change"), Invert and the range's two ends. Every choice
 * redraws the globe at once; the dialog stays open as the panel redraws.
 */
function changeColoursButton(grid: ChangeGrid, units: string | undefined, target: RangeTarget): HTMLButtonElement {
  const unitText = changeUnitText(grid, units)
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'analyze-change-palette'
  button.setAttribute('aria-haspopup', 'dialog')
  button.setAttribute('aria-expanded', 'false')
  const label = t('research.change.coloursAria')
  button.setAttribute('aria-label', label)
  button.title = label
  const swatch = document.createElement('span')
  swatch.className = 'analyze-change-palette-ramp'
  swatch.setAttribute('aria-hidden', 'true')
  swatch.style.background = changeGradient(changeViewState().colours, 'to top')
  button.appendChild(swatch)
  const ramp: PaletteRamp = {
    palettes: CHANGE_PALETTE_IDS,
    gradient: (d, direction) => changeGradient({ palette: d.palette, inverted: !!d.inverted }, direction),
    limits: () => target.shown() ?? grid.range,
    setLimits: (min, max) => {
      const range: ChangeRange = { min, max }
      if (!validRange(range)) return false
      target.set(range)
      trail({ type: 'change', data: { range, summary: `Colour range of the change set to ${min} to +${max}${unitText ? ` ${unitText}` : ''}` } })
      redrawPanel()
      return true
    },
    invalid: t('research.change.rangeInvalid'),
    units: unitText,
  }
  button.addEventListener('click', () => {
    const c = changeViewState().colours
    changeDialog = togglePaletteDialog({
      anchor: button,
      beside: button.closest<HTMLElement>('.analyze-panel'),
      title: t('research.change.coloursTitle'),
      accent: 'base',
      scale: null,
      ramp,
      display: { ...DEFAULT_DISPLAY, palette: c.palette, inverted: c.inverted },
      onChange: (d) => {
        const colours = { palette: d.palette, inverted: !!d.inverted }
        setChangeView({ colours })
        trail({ type: 'change', data: { colours, summary: `Colours of the change map: ${d.palette === 'source' ? 'RdBu' : d.palette}${d.inverted ? ', inverted' : ''}` } })
        redrawPanel()
      },
      onClose: () => {
        changeDialog = null
      },
    })
  })
  // The panel redrew under an open dialog: it moves onto this button.
  changeDialog?.setAnchor(button, ramp)
  return button
}

/**
 * AYNI (owner, 2026-10-20: "allow the user to change the min/max change
 * values"): the colour range's two ends, typed in the change's units. The
 * automatic range is ±limit from the first frame drawn; the viewer's lasts
 * until the mode or the baseline changes (another reading, other units).
 * Both ends must straddle zero so no change stays white.
 */
function renderChangeRange(grid: ChangeGrid, units: string | undefined, rerender: (focusSelector: string) => void, target: RangeTarget): HTMLElement {
  const own = target.own()
  const unitText = changeUnitText(grid, units)
  const row = document.createElement('div')
  row.className = 'analyze-change-range'
  row.setAttribute('role', 'group')
  row.setAttribute('aria-label', t('research.change.rangeAria'))
  const label = document.createElement('span')
  label.className = 'analyze-change-label'
  label.textContent = t('research.change.range')
  const problem = document.createElement('p')
  problem.className = 'analyze-change-hint analyze-change-range-problem'
  problem.setAttribute('role', 'alert')
  problem.hidden = true
  const apply = (cls: string): void => {
    const next: ChangeRange = { min: Number(min.value), max: Number(max.value) }
    if (!validRange(next) || min.value.trim() === '' || max.value.trim() === '') {
      min.setAttribute('aria-invalid', String(min.value.trim() === '' || !(next.min < 0)))
      max.setAttribute('aria-invalid', String(max.value.trim() === '' || !(next.max > 0)))
      problem.textContent = t('research.change.rangeInvalid')
      problem.hidden = false
      return
    }
    // Redrawing takes the focused box away, whose blur fires change again: only a new range redraws.
    if (next.min === grid.range.min && next.max === grid.range.max) return
    target.set(next)
    trail({ type: 'change', data: { range: next, summary: `Colour range of the change set to ${next.min} to +${next.max}${unitText ? ` ${unitText}` : ''}` } })
    rerender(`.${cls}`)
  }
  const field = (cls: string, value: number, aria: MessageKey): HTMLInputElement => {
    const input = document.createElement('input')
    input.type = 'number'
    input.step = 'any'
    input.inputMode = 'decimal'
    input.className = `analyze-change-range-input ${cls}`
    input.value = String(Number(value.toPrecision(6)))
    input.setAttribute('aria-label', t(aria, { units: unitText || '—' }))
    input.addEventListener('change', () => apply(cls))
    return input
  }
  const min = field('analyze-change-min', grid.range.min, 'research.change.rangeMinAria')
  const max = field('analyze-change-max', grid.range.max, 'research.change.rangeMaxAria')
  const to = document.createElement('span')
  to.className = 'analyze-change-range-to'
  to.textContent = t('research.change.rangeTo')
  const unit = document.createElement('span')
  unit.className = 'analyze-change-range-units'
  unit.textContent = unitText
  row.append(label, min, to, max, unit)
  if (own) {
    const auto = actionButton(t('research.change.rangeAuto'), () => {
      target.set(null)
      trail({ type: 'change', data: { range: null, summary: 'Colour range of the change back to automatic' } })
      rerender('.analyze-change-min')
    })
    auto.classList.add('analyze-change-range-auto')
    row.appendChild(auto)
  }
  const wrap = document.createElement('div')
  wrap.className = 'analyze-change-range-wrap'
  wrap.append(row, problem)
  if (own) wrap.appendChild(caption(t('research.change.rangeOwn')))
  return wrap
}

/** Coverage as prose rather than a tile, because the number that
 *  matters is what fraction of the region carried data at all — a mean
 *  over 3% of a box is a different claim from a mean over 90% of it. */
function renderCoverage(stats: RegionStats): HTMLElement {
  const p = document.createElement('p')
  p.className = 'analyze-coverage'
  p.textContent = t('analyze.coverage', {
    percent: formatNumber(stats.coverage * 100, { maximumSignificantDigits: 2 }),
    count: formatNumber(stats.count),
  })
  return p
}

/**
 * How wide a bar is, in the dataset's own units.
 *
 * The chart aggregates several luma codes per bar (see
 * `analyzeCharts.renderHistogram` for why the transport makes that the
 * honest resolution), so the reader is told what a bar actually covers
 * rather than left to infer it from an unlabelled axis.
 */
function renderHistogramCaption(scale: DisplayColorScale): HTMLElement {
  const p = document.createElement('p')
  p.className = 'analyze-caption'
  p.textContent = t('analyze.histogram.caption', {
    width: formatStatValue(histogramBucketValueWidth(scale), scale.units),
  })
  return p
}

/**
 * The uncertainty, stated next to the numbers rather than in a footnote.
 *
 * One luma step is the whole resolution of the transport, and the
 * measured encoder RMSE is about the same size, so that step is the
 * honest floor on any single value here. Saying so on the surface is
 * the difference between a statistic and a claim.
 */
function renderPrecisionNote(scale: DisplayColorScale, options?: DatasetOverlayOptions): HTMLElement {
  const p = document.createElement('p')
  p.className = 'analyze-precision'
  const enc = options?.releaseEncoding
  // AYNI: a log release has no single width: one code is a fixed share of the value (value error audit, 2026-10-19).
  if (enc?.kind === 'luma8-log') {
    const ratio = logCodeRatio(enc)
    p.textContent = ratio !== null ? t('analyze.precisionRelative', { percent: formatNumber((ratio - 1) / 2, { style: 'percent', maximumSignificantDigits: 2 }) }) : ''
    return p
  }
  const step = scaleCodeStep(scale) ?? Math.abs(scale.vmax - scale.vmin) / 255
  // AYNI: a release states its ± where it can: half a step plus the codec's
  // 99th percentile (valueFidelity.ts), in the scale's units when the scale
  // restates them; without a published codec error, the rounding part only, and it says so.
  if (enc?.kind === 'luma8-linear') {
    const mid = enc.vmin + (enc.vmax - enc.vmin) / 2
    const u = readingUncertainty(enc, mid)
    if (u) {
      // The scale and the release's own scale span the same codes; they differ only by a restated unit.
      const own = releaseColorScale(enc)
      const factor = Math.abs((scale.vmax - scale.vmin) / (own.vmax - own.vmin)) || 1
      const error = formatStatValue(roundUncertainty(u.u * factor).u, scale.units)
      p.textContent = t(u.complete ? 'analyze.precisionMeasured' : 'analyze.precisionRoundingOnly', { error })
      return p
    }
  }
  p.textContent = t('analyze.precision', { step: formatStatValue(step, scale.units) })
  return p
}

function renderExport(src: AnalyzeSource): HTMLElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = 'analyze-export'
  btn.textContent = t('analyze.export')
  btn.addEventListener('click', () => {
    const part = statsCsv(src)
    if (part) downloadCsv(part.name, part.text)
  })
  return btn
}

/** AYNI (S8): a CSV as its button saves it: the browser's file name and the text. */
interface CsvPart {
  readonly name: string
  readonly text: string
}

/** The region statistics as a CSV; null with nothing measured. */
function statsCsv(src: AnalyzeSource): CsvPart | null {
  if (!lastResult) return null
  return {
    name: ayniCsvFilename('stats', src.datasetTitle(), new Date(), scopeToValue(scope)),
    text: buildCsvText(lastResult.stats, lastResult.hist, lastResult.scale, {
      datasetTitle: src.datasetTitle(),
      scopeLabel: scopeToValue(scope),
      provenance: src.provenance?.() ?? undefined,
      baseline: csvBaseline(),
    }),
  }
}

/** The latitude profile as a CSV; null without one. */
function zonalCsv(): CsvPart | null {
  if (!lastZonal) return null
  return {
    // AYNI: named for what it is, of what, and when (pinsCsv.ts).
    name: ayniCsvFilename('zonal', source?.datasetTitle() ?? null, new Date(), scopeToValue(scope)),
    text: buildZonalCsvText(lastZonal.samples, lastZonal.scale, {
      // The same region label the statistics export carries — this
      // profile is of that region, not of a separate pick.
      datasetTitle: source?.datasetTitle() ?? null,
      scopeLabel: scopeToValue(scope),
      provenance: source?.provenance?.() ?? undefined,
      baseline: csvBaseline(),
    }),
  }
}

/** The line profile as a CSV; null without a line. */
function transectCsv(): CsvPart | null {
  if (!lastTransect) return null
  return {
    name: ayniCsvFilename('line', source?.datasetTitle() ?? null, new Date()),
    text: buildTransectCsvText(lastTransect.samples, lastTransect.scale, {
      datasetTitle: source?.datasetTitle() ?? null,
      scopeLabel: t('analyze.transect.title'),
      provenance: source?.provenance?.() ?? undefined,
    }),
  }
}

/** AYNI (S8): what the bundle button last said; the panel redraws often, so it is kept here. */
let bundleStatus = ''
function setBundleStatus(text: string): void {
  bundleStatus = text
  const el = root?.querySelector<HTMLElement>('.analyze-bundle-status')
  if (el) el.textContent = text
}

/** The comparison on screen as a CSV (set while Compare shows one). */
let compareCsv: (() => CsvPart) | null = null

/**
 * AYNI (S8): every Analyze CSV the panel can produce now, for the
 * reproducibility bundle: the statistics, the latitude and line profiles
 * and Compare, each only when it is on screen. None on a plan whose
 * Analyze exports are locked (the pins' CSV is the pins' own).
 */
export function analyzeBundleCsvs(): BundleCsv[] {
  if (!root || !source || analysisExportsLocked()) return []
  const out: BundleCsv[] = []
  const add = (kind: BundleCsv['kind'], part: CsvPart | null) => {
    if (part) out.push({ kind, text: part.text })
  }
  add('statistics', statsCsv(source))
  add('latitude', zonalCsv())
  add('line', transectCsv())
  add('compare', compareCsv?.() ?? null)
  return out
}

/**
 * AYNI: the frame Analyze would measure now and the instant it shows, for
 * the chatbot's statistics (`analyzeChat.ts`), so its numbers are the
 * panel's; null without a dataset that carries values.
 */
export function analysisFrameNow(): { frame: NonNullable<ReturnType<AnalyzeSource['frame']>>; frameTime: string | null } | null {
  const frame = source?.frame() ?? null
  return frame ? { frame, frameTime: source?.frameTime?.() ?? null } : null
}

/** Exposed for tests: the numbers currently on screen. */
export function currentResult(): RegionStats | null {
  return lastResult?.stats ?? null
}

// --- AYNI: Lab space (docs/AYNI_XR_RESEARCH_TIER_PLAN.md, decision R5) ----------

/** A region as Lab space stores it (`labSpace.checkRegion`): `dataset`, `view`, `zone:…`, `named:…` or `box:n,s,w,e`. */
function labRegionOf(s: AnalyzeScope): string {
  if (s.kind === 'box') {
    const r = (v: number) => String(Math.round(v * 10_000) / 10_000)
    return `box:${r(s.bounds.n)},${r(s.bounds.s)},${r(s.bounds.w)},${r(s.bounds.e)}`
  }
  return scopeToValue(s)
}

function scopeOfLabRegion(region: string): AnalyzeScope {
  if (region.startsWith('box:')) {
    const [n, s, w, e] = region.slice(4).split(',').map(Number)
    if ([n, s, w, e].every(Number.isFinite)) return { kind: 'box', bounds: { n, s, w, e } }
    return { kind: 'dataset' }
  }
  return valueToScope(region)
}

/** The baseline held now, as a definition Lab space can share (never its numbers); null when none is held. */
export type LabBaselineDraft =
  | { kind: 'frame'; datasetId: string; datasetTitle: string; query: string; frameLabel: string; region: string; regionLabel: string }
  | { kind: 'period'; datasetId: string; datasetTitle: string; from: number | null; to: number | null; grouping: PeriodGrouping; span: string; region: string; regionLabel: string }

export function labBaselineNow(): LabBaselineDraft | null {
  if (!researchToolsAllowed()) return null
  const label = () => root?.querySelector<HTMLSelectElement>('.analyze-scope-select')?.selectedOptions[0]?.textContent ?? scopeToValue(scope)
  if (periodState?.clim && periodState.field) {
    const p = periodState
    return {
      kind: 'period', datasetId: p.datasetId, datasetTitle: p.datasetTitle ?? '',
      from: p.period?.from ?? null, to: p.period?.to ?? null, grouping: p.calendar.grouping, span: p.span,
      region: labRegionOf(scope), regionLabel: label(),
    }
  }
  if (baseline && heldLab?.for === baseline) {
    return {
      kind: 'frame', datasetId: baseline.datasetId, datasetTitle: baseline.datasetTitle ?? '', query: heldLab.query,
      frameLabel: baseline.frameLabel ?? '', region: heldLab.region, regionLabel: baseline.regionLabel,
    }
  }
  return null
}

/**
 * Set up a shared baseline's definition on the dataset and frame now on
 * the globe (the caller has opened its view): Analyze opens on Baseline
 * over the same region; a frame is held from the numbers measured here and
 * now, a period starts reading its frames. Nothing is taken from the
 * sharer's numbers. Answers what happened.
 */
export function applyLabBaseline(def: { kind: 'frame'; region: string } | { kind: 'period'; region: string; from: number | null; to: number | null }): 'held' | 'reading' | 'opened' | 'refused' {
  if (!researchToolsAllowed() || !dataToolsAllowed()) return 'refused'
  scope = scopeOfLabRegion(def.region)
  baselineKind = def.kind
  if (def.kind === 'period') periodChoice = def.from !== null && def.to !== null ? { from: def.from, to: def.to } : null
  setAnalyzeTab('baseline')
  openAnalyzeUI(null, scope)
  const press = root?.querySelector<HTMLButtonElement>(def.kind === 'frame' ? '.analyze-baseline-hold' : '.analyze-period-start')
  if (!press || press.disabled) return 'opened'
  press.click()
  return def.kind === 'frame' ? 'held' : 'reading'
}

// --- AYNI: the AYNI chatbot's research commands (AYNI 3D's research and education contract, 2026-10-07) ---
//
// The chatbot works the panel the way a person does: it sets what a control
// would set, opens the panel on the tab, and reads back what the panel
// computed, so a number it says is the number on screen (`analyzeChat.ts`
// composes these). Gating (the research tools, a meeting's audience) is the
// executor's.

/** The region the panel measures now. */
export function analyzeScope(): AnalyzeScope {
  return scope
}

/** Which baseline the Baseline tab shows, and the period's years (null: the default for the dataset). */
export function prepareBaseline(kind: BaselineKind, period: ClimatePeriod | null = null): void {
  baselineKind = kind
  if (kind === 'period') periodChoice = period
  setAnalyzeTab('baseline')
}

/** Press a button of the open panel, as a click would; false when it is not there (or disabled). */
export function pressInAnalyze(selector: string): boolean {
  const button = root?.querySelector<HTMLButtonElement>(selector)
  if (!button || button.disabled || button.hidden) return false
  button.click()
  return true
}

/**
 * Whether a period can be read on the dataset on the globe, before any is
 * started: no time axis, classes, or the period's own refusal
 * (`checkPeriod`: `empty`, `tooFew`, `tooMany`).
 */
export function periodRefusalNow(period: ClimatePeriod | null): 'no-time-axis' | 'no-values' | 'empty' | 'tooFew' | 'tooMany' | null {
  const axis = source?.timeAxis?.() ?? null
  const frame = source?.frame() ?? null
  if (!axis) return 'no-time-axis'
  if (!frame || (frame.scale as { release?: { kind?: string } }).release?.kind === 'luma8-classified') return 'no-values'
  const cal = calendarFor(axis)
  const chosen = cal.grouping === 'window' ? null : (period ? clampPeriod(axis, period) : null) ?? defaultPeriod(axis)
  return checkPeriod(periodFrames(axis, cal, chosen, axis.usable), cal).refusal
}

/** The period being read (or read) was started by the chatbot: its completion is the chatbot's in the record. */
export function markPeriodByAssistant(): void {
  if (periodState) periodState.byAssistant = true
}

/** Read the period's missing frames one by one (playback pauses), as "Read the missing frames" does. */
export function stepPeriodNow(): boolean {
  const axis = source?.timeAxis?.() ?? null
  const state = periodState
  if (!axis || !state || state.clim || state.stepping || source?.datasetId() !== state.datasetId) return false
  void stepPeriod(axis)
  return true
}

/** "Use the frames read": finish with what has been read, once every group has 80 % of its frames. */
export function finishPeriodEarlyNow(): boolean {
  const state = periodState
  if (!state || state.clim || !state.run.enough()) return false
  finishPeriod(true)
  return true
}

/** Let go of the held baseline (a frame or a period) and its change, as Clear does. */
export function clearBaselineNow(): void {
  const had = baseline !== null || periodState !== null
  baseline = null
  clearPeriod()
  setChangeView({ mode: null, field: null })
  if (had) trail({ type: 'baseline', data: { kind: 'cleared', summary: 'Baseline cleared' } })
  const host = root?.querySelector('.analyze-body') as HTMLElement | null
  if (host) refresh(host)
}

/** The baseline held now, as the chatbot reports it (`baseline.status`). */
export function chatBaselineState(): ChatBaselineState {
  const state = periodState
  if (state) {
    const run = state.run
    const clim = state.clim
    const cal = state.calendar
    const visited = expectedGroups(cal)
    const minYears = clim ? Math.min(...visited.map((g) => clim.yearsPerGroup[g] ?? 0)) : run.check.minYears
    const axis = source?.timeAxis?.() ?? null
    const def = axis ? defaultPeriod(axis) : null
    return {
      held: true,
      kind: 'period',
      span: state.span,
      from: state.period?.from ?? null,
      to: state.period?.to ?? null,
      isDefault: !state.period || (!!def && def.from === state.period.from && def.to === state.period.to),
      grouping: cal.grouping,
      read: run.read,
      total: run.total,
      enough: run.enough(),
      complete: clim !== null,
      sigma: run.check.sigma,
      minYears,
      skipped: run.check.skipped,
      factor: clim?.factor ?? run.factor,
      early: clim !== null && run.read < run.total,
      groups: cal.groups,
      flooredCells: clim ? clim.flooredCells : null,
      cellsWithSigma: clim ? clim.cellsWithSigma : null,
      units: state.units ?? null,
      method: periodMethodText(cal.grouping),
      stepping: state.stepping,
    }
  }
  if (baseline) {
    return { held: true, kind: 'frame', span: baseline.frameLabel, region: scopeEnglish(baseline.regionKey.split('|')[0]), units: baseline.units ?? null, fit: root ? lastFit : null }
  }
  return { held: false }
}

/**
 * Show the change from the held baseline on the globe (On the globe: Off ·
 * Change · % change · σ change), or say why not; the caller opens the panel
 * on Baseline, where the change is drawn and explained.
 */
export function setChangeModeNow(mode: ChangeMode | null): { ok: true; units: string | null } | { ok: false; reason: 'no-baseline' | 'sigma-needs-period' | 'negative' | 'interval' } {
  const view = changeViewState()
  if (mode !== null) {
    if (!view.field) return { ok: false, reason: 'no-baseline' }
    if (mode === 'sigma' && !(periodState?.clim?.hasSigma && view.field === periodState.field)) return { ok: false, reason: 'sigma-needs-period' }
    if (mode === 'percent') {
      const frame = source?.frame() ?? null
      const refusal = frame ? percentRefusal(frame.scale) : null
      if (refusal) return { ok: false, reason: refusal }
    }
  }
  if (changeViewState().other) {
    compareMode = null
    source?.compareActive?.(null)
    setChangeView({ other: null })
  }
  setChangeView({ mode })
  const f = changeViewState().field
  const against = f?.period ? `the ${f.period.label} climatology` : `the baseline held at ${f?.frameLabel ?? 'a frame without a date'}`
  const what = mode === 'difference' ? 'change' : mode === 'percent' ? '% change' : 'σ change'
  trail({ type: 'change', data: { mode: mode ?? 'off', against, summary: mode ? `On the globe: ${what} from ${against}` : 'On the globe: values (change off)' } })
  const host = root?.querySelector('.analyze-body') as HTMLElement | null
  if (host) refresh(host)
  return { ok: true, units: f?.units ?? null }
}

/** What Compare compares (as its controls would set it); the next render measures it. */
export function setCompareChoice(choice: { source: CompareSource; id?: string; pairing?: FramePairing; mode?: CompareMode | null }): void {
  if (choice.source !== compareSource || (choice.id && choice.id !== compareId)) {
    if (compareMode && changeViewState().other) takeChangeOff()
    compareMode = null
  }
  compareSource = choice.source
  if (choice.id) compareId = choice.id
  if (choice.pairing) comparePairing = choice.pairing
  if (choice.mode !== undefined) compareMode = choice.mode
  compareAskedPercent = choice.mode === 'percent'
  compareChat = null
  chatOwned.compare = true
  setAnalyzeTab('compare')
}

/** What Compare found on its last render (null before one). */
export function compareNow(): { ok: true; result: ChatCompareResult } | { ok: false; reason: string } | null {
  return compareChat
}

/** The overlays and globes Compare can use now, by id, for the chatbot to name one. */
export function compareChoices(): { id: string; kind: CompareSource; title: string }[] {
  return (source?.compareCandidates?.() ?? []).map((c) => ({ id: c.id, kind: c.kind ?? 'overlay', title: c.title }))
}

/**
 * Set the Analyze Line by its ends (the chatbot's `hovmoller.summary`):
 * the panel opens on Profiles, the line is drawn on the globe with
 * draggable ends, and the Line profile and the Line over time follow it.
 * False when there is no globe to draw on.
 */
export function setAnalyzeLine(from: { lat: number; lon: number }, to: { lat: number; lon: number }): boolean {
  setAnalyzeTab('profiles')
  if (!root) openAnalyzeUI(null)
  const picker = source?.transect?.()
  if (!picker || !transectHost) return false
  clearTransect()
  const onChange = (ends: TransectEndpoints | null): void => {
    transectEnds = ends
    transectArmed = false
    renderTransectSection()
  }
  const ends = { from: { ...from }, to: { ...to } }
  setHovmollerByAssistant(true)
  if (picker.set) picker.set(ends, onChange)
  // The picker calls back at once; without one, the line is the panel's alone.
  if (!transectEnds) onChange(ends)
  return true
}

/** Whether a Line is drawn in the panel now. */
export function analyzeLineSet(): boolean {
  return transectEnds !== null
}

/** Focus the Bundle button below the tabs (the user presses it); false when it is not there. */
export function focusBundleButton(): boolean {
  const button = root?.querySelector<HTMLButtonElement>('.analyze-bundle-open')
  if (!button) return false
  button.scrollIntoView?.({ block: 'nearest' })
  // A ring the user can see whichever way the focus came (a script's focus shows no :focus-visible).
  button.classList.add('analyze-attention')
  button.addEventListener('blur', () => button.classList.remove('analyze-attention'), { once: true })
  button.focus()
  return document.activeElement === button
}
